import React, { useState, useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import { getChartColors } from "theme";
import api from "services/api";
import { normalizePhoneNumber } from "helpers/ServiceHelpers";

const formatCurrency = (value) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Linguist Overview Report - Dashboard view with key linguist metrics
 */
export const LinguistOverview = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });
  
  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const handleLinguistRetentionClick = useCallback(
    async (data) => {
      try {
        let retentionData = data?.retentionDetails;

        if (!retentionData) {
          const response = await api.get(
            "/reports/financial/linguist-retention-details",
            { params: filters },
          );
          retentionData = response.data.data;
        }

        setDrillDown({
          open: true,
          title: "Linguist Retention Analysis",
          subtitle: `${retentionData.summary.retentionRate}% retention • ${retentionData.summary.inactive} inactive`,
          description: (
            <Box
              component="div"
              sx={{
                typography: "body2",
                lineHeight: 1.6,
                color: "text.secondary",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 1.5, color: "info.main" }}
              >
                Linguist Retention Breakdown
              </Typography>
              <Typography variant="body2" paragraph>
                This analysis compares active linguists between the{" "}
                <strong>current period</strong> (filtered dates) and the{" "}
                <strong>previous period</strong> of equal length.
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                • <strong>Retained linguists:</strong> Took assignments in BOTH
                periods (continued working)
                <br />• <strong>Inactive linguists:</strong> Took assignments in
                previous period but NONE in current period (stopped taking work
                - dates shown are from previous period)
                <br />• <strong>New linguists:</strong> Started taking
                assignments in the current period only (no previous history)
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "background.alt",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" display="block">
                  <strong>Retention Rate:</strong>{" "}
                  {retentionData.summary.retentionRate}%
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Attrition Rate:</strong>{" "}
                  {retentionData.summary.attritionRate}%
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Retained:</strong> {retentionData.summary.retained}{" "}
                  linguists
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>Inactive:</strong> {retentionData.summary.inactive}{" "}
                  linguists
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>New:</strong> {retentionData.summary.new} linguists
                </Typography>
              </Box>
            </Box>
          ),
          data: [
            ...retentionData.retainedLinguists.map((l) => ({
              ...l,
              status: "Retained",
            })),
            ...retentionData.inactiveLinguists.map((l) => ({
              ...l,
              status: "Inactive",
            })),
            ...retentionData.newLinguists.map((l) => ({ ...l, status: "New" })),
          ],
          columns: [
            { field: "name", headerName: "Linguist Name", width: 200 },
            {
              field: "status",
              headerName: "Status",
              width: 120,
              renderCell: (row) => (
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      row.status === "Retained"
                        ? "success.main"
                        : row.status === "Inactive"
                          ? "error.main"
                          : "info.main",
                    fontWeight: 600,
                  }}
                >
                  {row.status}
                </Typography>
              ),
            },
            { field: "servicesCount", headerName: "Services", width: 100 },
            {
              field: "revenue",
              headerName: "Revenue Generated",
              width: 150,
              renderCell: (row) =>
                row.revenue
                  ? `$${row.revenue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            {
              field: "pay",
              headerName: "Total Pay",
              width: 130,
              renderCell: (row) =>
                row.pay
                  ? `$${row.pay?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-",
            },
            {
              field: "activityChange",
              headerName: "Activity Change",
              width: 140,
              renderCell: (row) => {
                if (row.activityChange == null) return "-";
                const changeSymbol = row.activityChange > 0 ? "+" : "";
                return `${changeSymbol}${row.activityChange} services`;
              },
            },
            {
              field: "lastServiceDate",
              headerName: "Last Service Date",
              width: 140,
              renderCell: (row) =>
                row.lastServiceDate
                  ? new Date(row.lastServiceDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "-",
            },
          ],
          summary: {
            metrics: [
              {
                label: "Retention Rate",
                value: `${retentionData.summary.retentionRate}%`,
                color:
                  retentionData.summary.retentionRate >= 90
                    ? "success.main"
                    : "warning.main",
              },
              {
                label: "Attrition Rate",
                value: `${retentionData.summary.attritionRate}%`,
                color:
                  retentionData.summary.attritionRate <= 10
                    ? "success.main"
                    : "error.main",
              },
              {
                label: "Retained Linguists",
                value: retentionData.summary.retained,
                color: "success.main",
              },
              {
                label: "Inactive Linguists",
                value: retentionData.summary.inactive,
                color: "error.main",
              },
              {
                label: "New Linguists",
                value: retentionData.summary.new,
                color: "info.main",
              },
            ],
          },
        });
      } catch (error) {
        console.error("Error fetching linguist retention details:", error);
      }
    },
    [filters],
  );

  const metricsConfig = useCallback(
    (data) => {
      return [
        {
          groupTitle: "Linguist Performance & Retention",
          metrics: [
            {
              title: "Linguist Retention",
              value: `${data.linguistRetentionRate?.toFixed(2)}%`,
              subtitle: "Active linguists retained",
              color:
                data.linguistRetentionRate >= 90
                  ? "success.main"
                  : "warning.main",
              tooltipTitle: "Linguist Retention Rate",
              tooltipContent:
                `Linguist Retention Rate = (Linguists who stayed active ÷ Total active linguists from last period) × 100

This measures what percentage of your linguist pool remains active and continues taking assignments with your company from one period to the next.

DETAILED DEFINITION:
• Active in period 1: Linguist took at least 1 assignment in the previous period
• Active in period 2: Same linguist took at least 1 assignment in the current period
• Retained: Both periods = this linguist counts toward retention rate
• Not retained: Period 1 but not period 2 = this linguist churned (counts as loss)
• New: Only period 2 = not counted in retention calculation (tracked separately)

WORKED EXAMPLES:
Example 1 (90% retention):
• Previous quarter: 50 active linguists
• Current quarter: 45 of those same linguists are still active (new recruits don't count toward retention %)
• Retention rate = 45 ÷ 50 = 90% ✓ (Excellent)
• 5 linguists churned (resigned, went inactive, stopped taking work)

Example 2 (75% retention with attrition risk):
• Previous: 60 active linguists
• Current: Only 45 of those retained (15 churned)
• Retention rate = 45 ÷ 60 = 75% ⚠️ (Below healthy threshold)
• Even if you recruited 20 new linguists, you're only at 65 active (treading water)
• This indicates process problems, not just growth dynamics

WHY LINGUIST RETENTION IS YOUR TOP STRATEGIC METRIC:
Operational Impact:
• Service Continuity: Churned linguists disrupt client relationships and project continuity
• Quality Consistency: Retained linguists know your clients' terminology, style guides, preferences; new linguists require expensive ramp-up
• Assignment Fill Rate: High retention = predictable linguist availability; high churn = constant crisis mode
• Delay Reduction: Familiar linguist can start a job same-day; new linguist requires intake briefing, certification verification
• Compliance: Retained linguists already have background checks, W9s, certifications on file

Financial Impact:
• Recruiting Cost: Each replacement costs $500-2000 in recruiting, vetting, background check, W9 processing (variable by program)
• Onboarding Time: Typically 2-4 weeks to certify new linguist in your style and systems; lost productivity
• Training: Specialized clients require training; each new linguist must be trained (some clients refuse new linguists on their account)
• Margin Pressure: New linguists often get lower assignment volume and higher error rates, impacting revenue and margin

Client Impact:
• Continuity Preference: 70%+ of clients explicitly request "linguist continuity" in RFPs
• Quality Risk: Unfamiliar linguist = higher QA rework, revision requests, client dissatisfaction
• Relationship Risk: Clients who have relationship with a specific linguist may move work to that linguist's new employer
• Upsell Risk: Relationships take months to deepen; early churn prevents value-add services and premium pricing

Market/Reputation Impact:
• Linguists Tell Stories: Disgruntled (churned) linguists tell peers about pay, scheduling, professionalism; your reputation impacts recruiting success
• Competitive Hiring: "We lost our best linguists to Competitor X" signals market weakness
• Industry Network: Translation industry is small; reputation for churn spreads quickly

DECODING YOUR RETENTION METRICS:
90%+ retention:
✓ Pay rates are competitive
✓ Work distribution feels predictable
✓ Payment is reliable (on-time, accurate)
✓ Linguists feel respected and supported
✓ Operations run smoothly with minimal churn (your target)

80-89% retention (Good but watch closely):
⚠️ Some underlying friction exists
⚠️ Possible causes: seasonal work patterns (normal) OR emerging payment issues, competitive rate pressure, or changing work mix
⚠️ Action: Monitor next period; if dips below 80%, investigate root cause

70-79% retention (Concerning):
🔴 You're losing nearly 1 in 4 linguists per period
🔴 Likely causes: Pay is below market, work is inconsistent, payment lags developing, language skill mix not matching work demand
🔴 Impact: You're in constant recruitment crisis mode; new linguists never reach productivity
🔴 Urgent: Address root cause before it becomes death spiral

Below 70% retention (Crisis):
🔴 You're replacing >30% of your linguist base each period
🔴 This is unsustainable and indicates fundamental process problem
🔴 Likely causes: Recent pay cut, months-long payment delays, operational chaos, or loss of major client demand
🔴 Immediate action: Conduct linguist interviews to diagnose, adjust pay/process, consider bringing in interim linguist team if critical

ROOT CAUSE DIAGNOSIS FRAMEWORK:
If retention dropped this period, investigate in this order:

1) Payment Issues (Most common cause of churn)
   — Are payments on-time? (Check: # linguists with overdue balances)
   — Is payment accurate? (Check: # dispute cases this period)
   — Have you delayed payment in past 60 days? (This immediately triggers churn wave)
   → Solution: Audit payment queue, resolve disputes, communicate timeline if delayed

2) Market Rate Pressure (Second most common)
   — What's the going rate for your top linguist languages in your geography?
   — Have competitors recently raised rates? (Ask recent joiners)
   — Are you 10%+ below market for your top 5 languages?
   → Solution: Benchmark against 3+ competitors, consider targeted rate increases for at-risk languages

3) Work Consistency Issues
   — Are you distributing work fairly, or do favorite linguists get all work?
   — Do some linguists get feast-or-famine patterns?
   — Is work volume seasonal (normal) or erratic (problematic)?
   → Solution: Implement assignment fairness review, smooth supply/demand cycles

4) Operational Friction
   — How long does assignment approval take?
   — Do linguists wait weeks for payment after completing work?
   — Are there onboarding/certification blockers?
   → Solution: Streamline workflows, reduce turnaround time, reduce bureaucracy

5) Role/Work Mismatch
   — Are you assigning linguists to work outside their specialty?
   — Have you changed work mix (e.g., shift from translation to interpretation)?
   → Solution: Re-match linguists to appropriate work types, communicate changes

BENCHMARKS:
🟢 Excellent: 90%+ retention (you're doing everything right)
🟡 Good: 80-89% retention (acceptable, but monitor trends)
🔴 Concerning: 70-79% retention (investigate and fix)
🔴 Crisis: Below 70% retention (all-hands-on-deck problem-solving required)

RELATIONSHIP TO OTHER METRICS:
• Retention ↓ + Directory Linguists ↓ = You're shrinking and losing share of mind
• Retention ↓ + Average Margin ↓ = You're cutting rates to stay competitive, creating death spiral
• Retention ↓ + Revenue/Profit ↓ = Churn is eroding your revenue base
• Retention ↑ + Compliance ↑ = Your retained linguists are more likely to maintain certifications
• Retention ↑ + Earnings paid % ↑ = Better payment tracking reduces churn

LONG-TERM STRATEGY:
Month 1-3: Stabilize (Get to 85%+ retention)
• Address immediate payment/rate issues
• Conduct stay interviews with top linguists
• Fix operational friction points

Month 4-12: Strengthen (Get to 90%+)
• Implement preferential rates/assignments for high performers
• Create retention bonuses for multi-quarter tenure
• Develop advancement paths (senior linguist roles, mentorship programs)

IMMEDIATE ACTIONS IF RETENTION IS LOW:
1. Click the Retention Mix chart below to drill down by linguist cohort (Retained vs Inactive)
2. Schedule interviews with 5-10 recently-churned linguists (exit interview style)
3. Survey current linguists (NPS-style) on: pay fairness, work consistency, payment reliability, respect
4. Compare your rates to the 3 closest competitors
5. If you find clear root cause, communicate your fix plan to remaining linguists
6. Set target: Get to 85%+ within 60 days`,
              onClick: () => handleLinguistRetentionClick(data),
            },
          ],
        },
      ];
    },
    [handleLinguistRetentionClick],
  );

  const chartsConfig = useCallback(
    (data) => {
      const topRows = (data.topLinguists || []).slice(0, 10);
      const retentionMix = [
        {
          id: 1,
          label: "Retained",
          value: data.linguistRetentionRate || 0,
        },
        {
          id: 2,
          label: "Not Retained",
          value: Math.max(0, 100 - (data.linguistRetentionRate || 0)),
        },
      ];

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Linguists by Revenue"
              tooltipTitle="Top Linguists by Revenue"
              tooltipContent="Ranks linguists by total revenue generated in the selected period. Use this to identify core contributors, monitor workload concentration, and detect over-dependency on a small subset of providers."
              data={topRows.map((row) => ({
                linguistName: row.name,
                revenue: row.revenue || 0,
              }))}
              xAxisKey="linguistName"
              series={[
                {
                  dataKey: "revenue",
                  label: "Revenue",
                  color: chartColors.info,
                },
              ]}
              height={360}
              yAxisLabel="Revenue ($)"
              yAxisScaleType="linear"
              valueFormatter={formatCurrency}
              onBarClick={() => handleLinguistRetentionClick(data)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Retention Mix"
              tooltipTitle="Retention Mix"
              tooltipContent="Shows retained versus non-retained share of your linguist base. Use this to quickly gauge network stability and whether retention pressure is emerging."
              data={retentionMix}
              height={360}
              valueFormatter={(value) => `${(value || 0).toFixed(2)}%`}
              legendPosition="right"
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.info, handleLinguistRetentionClick],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [revenueSummaryResponse, retentionDetailsResponse] = await Promise.all([
      api.get("/reports/financial/revenue-summary", {
        params: activeFilters,
      }),
      api.get("/reports/financial/linguist-retention-details", {
        params: activeFilters,
      }),
    ]);

    const revenueData = revenueSummaryResponse.data?.data || {};
    const retentionDetails = retentionDetailsResponse.data?.data || {};

    return {
      success: true,
      data: {
        ...revenueData,
        // Keep Linguist Overview KPI + chart aligned with the same retention definition as drilldown.
        linguistRetentionRate:
          retentionDetails?.summary?.retentionRate ?? revenueData.linguistRetentionRate ?? 0,
        retentionDetails,
      },
    };
  }, []);

  return (
    <>
      <DashboardReport
        title="Linguist Overview"
        description="Key metrics about your linguist network, performance, and retention"
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Linguist Directory & Language Matrix
 */
export const LinguistDirectoryLanguageMatrix = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const openDirectoryDrawer = useCallback((rows, title, subtitle, description) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "linguistName", headerName: "Linguist", width: 220 },
        { field: "email", headerName: "Email", width: 240 },
        { field: "phone", headerName: "Phone", width: 140 },
        { field: "status", headerName: "Status", width: 120 },
        { field: "languageCount", headerName: "Languages", width: 100 },
        { field: "languages", headerName: "Language Matrix", width: 300 },
        {
          field: "availabilityProfile",
          headerName: "Availability",
          width: 170,
          renderCell: (row) =>
            `${row.phoneAvailable ? "Phone" : ""}${
              row.phoneAvailable && row.virtualAvailable ? ", " : ""
            }${row.virtualAvailable ? "Virtual" : ""}${
              (row.phoneAvailable || row.virtualAvailable) && row.inPersonAvailable
                ? ", "
                : ""
            }${row.inPersonAvailable ? "In-Person" : ""}` || "None",
        },
      ],
    });
  }, []);

  const openLanguageCoverageDrawer = useCallback((languages, title, subtitle, description) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: languages,
      columns: [
        {
          field: "language",
          headerName: "Language",
          width: 250,
          renderCell: (row) => (
            <Typography variant="body2" fontWeight="600">
              {row.language}
            </Typography>
          ),
        },
        {
          field: "count",
          headerName: "Active Linguists",
          width: 150,
          renderCell: (row) => (
            <Typography
              variant="body2"
              sx={{
                color: row.count === 1 ? "error.main" : row.count <= 3 ? "warning.main" : "success.main",
                fontWeight: 600,
              }}
            >
              {row.count}
            </Typography>
          ),
        },
        {
          field: "riskLevel",
          headerName: "Risk Level",
          width: 150,
          renderCell: (row) => {
            if (row.count === 1) {
              return (
                <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
                  ⚠️ Critical
                </Typography>
              );
            } else if (row.count <= 3) {
              return (
                <Typography variant="body2" sx={{ color: "warning.main", fontWeight: 600 }}>
                  ⚠️ High
                </Typography>
              );
            } else if (row.count <= 5) {
              return (
                <Typography variant="body2" sx={{ color: "info.main", fontWeight: 600 }}>
                  ➜ Medium
                </Typography>
              );
            }
            return (
              <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600 }}>
                ✓ Low
              </Typography>
            );
          },
        },
      ],
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [availabilityResponse, linguistsResponse] = await Promise.all([
      api.get("/reports/linguist/availability", { params: activeFilters }),
      api.get("/linguists"),
    ]);

    const availabilityRows = availabilityResponse.data?.data?.tableData || [];
    const linguists = linguistsResponse.data || [];
    const linguistsById = {};
    linguists.forEach((l) => {
      linguistsById[l.id] = l;
    });

    const rows = availabilityRows.map((row) => {
      const details = linguistsById[row.linguistId] || {};
      const rates = Array.isArray(details.LinguistLanguages)
        ? details.LinguistLanguages
            .map((item) => parseFloat(item.Rate || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        : [];

      return {
        ...row,
        email: details.Email || "",
        phone: details.PhoneNumber ? normalizePhoneNumber(details.PhoneNumber) : "",
        active: Boolean(details.Active),
        minRate: rates.length > 0 ? Math.min(...rates) : 0,
        maxRate: rates.length > 0 ? Math.max(...rates) : 0,
        availabilityProfile: `${row.phoneAvailable ? "Phone" : ""}${
          row.phoneAvailable && row.virtualAvailable ? ", " : ""
        }${row.virtualAvailable ? "Virtual" : ""}${
          (row.phoneAvailable || row.virtualAvailable) && row.inPersonAvailable
            ? ", "
            : ""
        }${row.inPersonAvailable ? "In-Person" : ""}`,
      };
    });

    const languageCoverage = {};
    rows.forEach((row) => {
      (row.languages || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((language) => {
          languageCoverage[language] = (languageCoverage[language] || 0) + 1;
        });
    });

    const allLanguages = Object.entries(languageCoverage)
      .map(([language, count]) => ({ language, count }))
      .sort((a, b) => b.count - a.count);

    const singleLinguistLanguages = allLanguages.filter((item) => item.count === 1);
    const twoLinguistLanguages = allLanguages.filter((item) => item.count === 2);
    const threeLinguistLanguages = allLanguages.filter((item) => item.count === 3);

    return {
      data: {
        rows,
        allLanguages,
        singleLinguistLanguages,
        twoLinguistLanguages,
        threeLinguistLanguages,
      },
    };
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const fullyAvailable = rows.filter(
        (row) => row.phoneAvailable && row.virtualAvailable && row.inPersonAvailable,
      );
      const multilingual = rows.filter((row) => (row.languageCount || 0) >= 2);
      const avgLanguageCount =
        rows.length > 0
          ? rows.reduce((sum, row) => sum + (row.languageCount || 0), 0) / rows.length
          : 0;

      return [
        {
          groupTitle: "Directory Health",
          metrics: [
            {
              title: "Directory Linguists",
              value: rows.length,
              subtitle: "Current and pending active pool",
              tooltipTitle: "Directory Linguists",
              tooltipContent:
                `Total linguists currently represented in the active availability directory for operational staffing.

DEFINITION:
This is your immediately-available talent pool for matching with incoming client assignments. Includes linguists with confirmed current status (active, pending, or in-onboarding), not historical or archived records.

Note: Does NOT include inactive or archived linguists. If your active directory is under 50 linguists, you may have insufficient capacity buffer for demand spikes or specialization needs.

DIRECTORY COMPOSITION:
Your "Directory Linguists" likely breaks down as:
• Active: 60-80% (taking regular assignments)
• Pending Activation: 10-20% (hired, in vetting/certification phase)
• On-Leave: 5-10% (temporarily inactive for renovations, sabbatical)
• Testing/Trial: 5-10% (evaluating fit before permanent addition)

USE THIS METRIC TO:
1. Monitor Network Scale & Capacity Growth
   — Is directory growing 5-10% per quarter? (Healthy)
   — Is directory flat or shrinking? (Retention or recruiting problem)
   — Example: Directory grew 50 → 65 (+30% YoY) = strong recruiting

2. Assess Capacity Against Demand
   — Typical rule: 1 actively-working linguist can handle 8-15 services per month
   — If you have 100 directory, expect 60-80 actively working
   — That's capacity for 480-1200 services/month
   — Is this enough for your service volume?

3. Plan Recruiting & Onboarding Pipeline
   — If you need 20% year-over-year growth: your recruiting pipeline must produce >20 linguists/quarter
   — Factor 40% attrition during onboarding (some don't complete certification)
   — So to add net 20 linguists, you need to recruit ~33, expect ~20 to make it through
   — Build recruiting pipeline 3-4 months ahead of growth targets

4. Benchmark Against Service SLAs
   — If your SLA requires 95% of orders filled within 24 hours
   — You need min. 150-300 linguists depending on language diversity and demand patterns
   — Under 50 linguists = too small for any critical language diversmall shops can serve 3-5 language pairs well
   → Small shops (20-50 linguists): specialize in 5-8 language pairs, become expert
   → Medium shops (50-200 linguists): diversify across 20-40 languages
   → Large shops (200+ linguists): nationwide/global coverage, 50+ languages

DIRECTORY SIZE BENCHMARKS:
🟢 Excellent Growth: 10-15% net new per year (recruiting/retention balanced)
🟡 Stable: 0-10% net new per year (maintaining status quo)
🟡 Geographic Launch: 20-50% new per year (expanding to new markets)
🔴 Concerning: Negative growth (more churn than hiring)
🔴 Crisis: >1/3 of directory churned in single quarter

EXAMPLE SCENARIOS:
Scenario 1: Director at 100, no growth 12 months
→ You're recruiting to replace churn, not growing
→ Investigate if churn is due to market saturation or internal problems
→ If intentional (focused on quality), OK; if unintentional, fix churn

Scenario 2: Growing 50 → 75 → 105 per quarter (exponential growth)
→ Can you quality-assure all new linguists?
→ Are you able to match increased supply with increased demand?
→ Risk: oversupply leads to underutilization and margin pressure

Scenario 3: Flat at 120, but quality improving
→ Monitor if "quality" means higher-productivity linguists (good)
→ Or means idle time increasing (bad)

KEY RELATIONSHIPS:
• Directory size ↑ + Retention ↑ = Healthy growth (you're adding new without losing old)
• Directory size ↑ + Retention ↓ = Concerning (more new hires can't replace churn, net increase masking problem)
• Directory size ↓ + Directory Languages ↑ = Consolidation strategy (fewer but more specialized linguists)
• Directory size ↓ + Retention ↓ = Crisis (shrinking and losing people)

WHAT OPTIMAL LOOKS LIKE:
Small Boutique (50-75 linguists):
• Specialize in 5-8 language pairs deeply
• Each linguist has average 2.5+ languages
• High-touch, premium service model
• Pay top 5% of market rates
• Retention 95%+

Medium Service Provider (100-250 linguists):
• Serve 30-50 languages
• Average 1.8-2.0 languages per linguist
• Balanced quality and volume
• Competitive market-rate pay
• Retention 85-90%

Large Provider (250+ linguists):
• Nationwide/global coverage
• 50-100+ languages (including rare languages)
• Average 1.2-1.5 languages per linguist (many mono-language specialists)
• Competitive market-rate, specialized surge pricing
• Retention 70-80% (normal for scale)

CALCULATING OPTIMAL SIZE FOR YOUR BUSINESS:
1. What service volume (# services/month) do you want?
2. How many languages do you need to support?
3. Back-calculate linguists needed:
   — (# services ÷ 10 calls/linguist/month) = raw linguist count
   — × 1.2-1.5 (accounting for specialization and idle time)
   — + 20% buffer for onboarding/churn
   = Your target directory size

Example: Want 1000 services/month across 30 languages
→ 1000 ÷ 10 = 100 linguists base
→ × 1.3 (specialization) = 130 linguists
→ × 1.2 (churn buffer) = 156 linguists target directory

IMMEDIATE ACTIONS:
1. Is your directory growing, stable, or shrinking? Track trend over 12 months
2. Compare actual to target size calculated above
3. If below target: accelerate recruiting
4. If above target: improve utilization or accept higher idle time
5. Monitor Retention Rate monthly; directory size means little if you're churning out as fast as hiring in`,

              onClick: () =>
                openDirectoryDrawer(
                  rows,
                  "Linguist Directory",
                  `${rows.length} linguists in scope`,
                  "Unified profile directory including contact, language, and availability details.",
                ),
            },
            {
              title: "Multi-Language Linguists",
              value: multilingual.length,
              subtitle: `${rows.length > 0 ? ((multilingual.length / rows.length) * 100).toFixed(1) : "0.0"}% of directory`,
              tooltipTitle: "Multi-Language Linguists",
              tooltipContent:
                `Count of linguists with two or more languages listed. This reflects cross-coverage capacity and scheduling flexibility.

DEFINITION:
"Multi-language" = linguist has 2+ languages certified or active in your system. Does NOT require equal proficiency; some may be primary skill + secondary capability.

BUSINESS VALUE OF MULTI-LANGUAGE CAPACITY:
1. Flexible Assignment Routing
   — Example: English-Spanish linguist can handle English→Spanish OR Spanish→English assignments
   → Reduces queue time; can fill more assignments from same linguist pool
   → vs. mono-language: English linguist = only English→target

2. Reduces Single-Language Bottlenecks
   — If Spanish linguist is fully booked on English→Spanish, can route to Spanish→English
   — Creates implicit load-balancing across language pairs
   → Example: 10 English-only linguists + 5 bilingual linguists = better fill rate than 15 English-only

3. Improves Utilization Efficiency
   — Linguist with 2 languages is idle less (can take from larger work pool)
   — Example: Spanish linguist busy 60% of time; Spanish+Portuguese available 85% of time
   → Fewer idle slots = better revenue per linguist = higher margin

4. Supports Client Retention During Language Shifts
   — Client adds new service language → can leverage existing relationship
   — Example: Long-term client adds Portuguese; you have Spanish-Portuguese linguist
   → vs. having to bring in new vendor

5. Risk Mitigation for Rare Languages
   — Example: Hmong language coverage via one generalist linguist = dangerous
   — Better: Primary Hmong specialist + secondary Hmong capability from related-language expert = backup

BENCHMARKS BY BUSINESS MODEL:
High-Volume Low-Cost:
🟡 Target: 20-30% multi-language
→ Focus on volume and speed; specialization acceptable

Quality-Premium Model:
🟢 Target: 50-70% multi-language
→ Flexibility and adaptability are differentiators

General Services Model:
🟢 Target: 40-60% multi-language
→ Balance specialization (depth) with flexibility (breadth)

CAUSES OF LOW MULTI-LANGUAGE %:
🔴 Specialization Strategy (intentional): Focused on depth, not breadth
🔴 Recruitment Challenge: Bilingual linguists harder to find; cost +20-30% more
🔴 Certification Barriers: Full re-certification per language discourages multi-language
🔴 Poor Incentive Structure: No bonus/premium for linguists adding second language

STRATEGIC INITIATIVES TO INCREASE %:
1. Secondary Language Certification (easiest to start)
   — Lightweight cert: 50 assignments vs. 200 for primary
   — Pay +10% premium for secondary language work
   — Expected: 5-10% of linguists add secondary within 12 months

2. Targeted Recruitment for Bilingual Candidates
   — Recruit from immigrant communities, universities with translation programs
   — Typical cost: +$200-500 per hire
   — Expected: 15-20% of new recruits bilingual vs. 5% baseline

3. Cross-Training Program
   — Subsidize training/certification for adjacent languages
   — Require 12-month commitment post-training
   — Cost: $300-1000 per linguist
   — Expected: 10-15% develop secondary within 18 months

4. Premium Pay Tier for Multi-Language Specialists
   — Pay 15-25% premium for documented secondary language capability
   — Cost: reduced margin on 10-15% of services
   — Expected: attracts qualified multi-language linguists

RELATIONSHIP TO OTHER METRICS:
• Multi-Language ↑ + Single-Linguist Languages ↓ = Reducing risk successfully
• Multi-Language ↑ + Directory Size ↓ = More concentrated expertise with fewer people
• Multi-Language ↑ + High Utilization Linguists ↓ = Better load balancing

IMMEDIATE ACTIONS:
1. Check if low multi-language is intentional (specialization strategy) or unintentional (recruiting challenge)
2. If unintentional: Pick one initiative above (secondary certification is easiest)
3. Monitor quarterly; track impact on utilization and Single-Linguist Languages %`,

              onClick: () =>
                openDirectoryDrawer(
                  multilingual,
                  "Multi-Language Linguists",
                  `${multilingual.length} linguists`,
                  "Providers with two or more listed languages, useful for coverage resilience and assignment flexibility.",
                ),
            },
            {
              title: "Fully Available",
              value: fullyAvailable.length,
              subtitle: "Phone + Virtual + In-Person",
              tooltipTitle: "Fully Available",
              tooltipContent:
                "Linguists currently available across all delivery channels (phone + virtual + in-person). A higher count indicates stronger dispatch flexibility and lower fill risk.\n\nFully available linguists are your most valuable operational asset because they can handle ANY assignment type without channel constraints. This maximizes fill rates and client satisfaction.\n\nChannel availability breakdown:\n• Phone: Suitable for quick clarifications, ad-hoc needs, low-prep work\n• Virtual: Cost-effective, scalable, works across time zones\n• In-Person: Required for legal proceedings, high-stakes negotiations, sensitive business\n\nBenchmarks:\n🟢 Excellent: 60%+ of directory fully available\n🟡 Good: 40-59% fully available\n🔴 Risk: Below 40% fully available\n\nIf fully-available % is low:\n• Identify which channels have gaps (e.g., many virtual-only linguists)\n• Invest in recruiting for underrepresented channels\n• Check if channel constraints are real or outdated profile data.",

              color: "success.main",
              onClick: () =>
                openDirectoryDrawer(
                  fullyAvailable,
                  "Fully Available Linguists",
                  `${fullyAvailable.length} linguists`,
                  "Linguists available on all channels: phone, virtual, and in-person.",
                ),
            },
            {
              title: "Avg Languages per Linguist",
              value: avgLanguageCount.toFixed(2),
              subtitle: "Average language depth",
              tooltipTitle: "Avg Languages per Linguist",
              tooltipContent:
                "Average number of languages per linguist in scope. This measures breadth of capability across your directory and indicates resilience against language-specific demand shocks.\n\nFormula: (Sum of all language counts) ÷ (Total linguists)\n\nBenchmarks:\n🟢 Excellent: 2.0+ avg languages per linguist\n🟡 Good: 1.5-1.99 avg languages\n🔴 Risk: Below 1.5 avg languages\n\nWhy this matters:\n• Low values suggest heavy reliance on single-language specialists, creating fill risk if demand shifts\n• High values indicate flexible capability pool that adapts to varying client needs\n• Impacts utilization efficiency (linguists with 2+ languages spend less idle time waiting for matching work)\n\nIf average is low:\n• Audit which languages have single-specialist coverage\n• Invest in upskilling linguists toward secondary certifications\n• Target recruiting for linguists with rare-language pairs (e.g., Mandarin + English)\n• Monitor single-language dependencies closely in capacity planning.",

              onClick: () =>
                openDirectoryDrawer(
                  rows,
                  "Directory Capability Depth",
                  `${avgLanguageCount.toFixed(2)} average languages`,
                  "Use language count and matrix to identify concentration risk by language demand.",
                ),
            },
          ],
        },
        {
          groupTitle: "Language Coverage",
          metrics: [
            {
              title: "Total Languages",
              value: (data.allLanguages || []).length,
              subtitle: "Unique languages in scope",
              tooltipTitle: "Total Languages",
              tooltipContent:
                "Total number of unique languages covered by the linguist directory. This represents your breadth of language service capability and geographic market reach.\n\nClick to view the full language catalog with linguist counts per language and color-coded supply risk levels.\n\nKey insights:\n• Breadth of language offering is a strategic differentiator for client acquisition and retention\n• More languages = ability to pursue more diverse clientele and geographic markets\n• But wide breadth with low depth (1-2 linguists per language) creates service risk\n\nBenchmarks:\n🟢 Excellent: Coverage of 25+ languages\n🟡 Good: Coverage of 15-24 languages\n🔴 Concerning: Coverage of fewer than 15 languages\n\nNote: Quality (depth) matters as much as breadth. A network with 50 languages but 80% single-linguist coverage is riskier than 20 languages with 3+ linguist backup for each.\n\nUse this alongside Single-Linguist Languages count to assess real delivery risk.",

              onClick: () =>
                openLanguageCoverageDrawer(
                  data.allLanguages || [],
                  "Language Coverage Matrix",
                  `${(data.allLanguages || []).length} languages`,
                  "Complete view of language availability with linguist counts and supply risk assessment.",
                ),
            },
            {
              title: "Single-Linguist Languages",
              value: (data.singleLinguistLanguages || []).length,
              subtitle: "Languages at highest risk",
              tooltipTitle: "Single-Linguist Languages",
              tooltipContent:
                "Number of languages served by only 1 linguist. These represent CRITICAL supply and continuity risk and should be your top recruitment/mitigation priority.\n\nWhy single-linguist is dangerous:\n• If that linguist becomes unavailable (illness, resignation, vacation), you cannot fulfill assignments\n• Zero backup means 100% fill-rate failure for that language until you find a replacement\n• Can trigger client SLA breaches, damage reputation, and drive churn\n• Limits scalability if client demand for that language grows\n\nRisk severity:\n🔴 CRITICAL: Any language served by only 1 linguist\n\nAction plan:\n1. Click to view the list of high-risk languages\n2. Treat these as emergency hiring priorities\n3. For each single-linguist language:\n   • Recruit a backup linguist as URGENT\n   • Begin cross-training initiatives for adjacent languages\n   • Document all process knowledge to prepare for succession\n   • Consider partnerships or subcontracting if recruiting takes time\n\nTarget state: Maximum 2-3 single-linguist languages, and ZERO within 12 months.",

              color: "error.main",
              onClick: () =>
                openLanguageCoverageDrawer(
                  data.singleLinguistLanguages || [],
                  "High-Risk Languages (Single Linguist Coverage)",
                  `${(data.singleLinguistLanguages || []).length} languages at risk`,
                  "Languages currently covered by only one linguist. These require immediate attention for staffing resilience.",
                ),
            },
            {
              title: "2-Linguist Languages",
              value: (data.twoLinguistLanguages || []).length,
              subtitle: "Languages with limited backup",
              tooltipTitle: "2-Linguist Languages",
              tooltipContent:
                "Number of languages served by exactly 2 linguists. These have limited redundancy and elevation risk if one linguist becomes unavailable.\n\nRisk profile:\n🟡 HIGH RISK: Losing 1 of 2 linguists = 50% capacity loss, single-point-of-failure for that language\n\nScenarios where 2-linguist coverage fails:\n• One linguist falls ill during competing assignments → fill-rate drops to 50%\n• Unexpected resignation → immediate crisis mode\n• Vacation/time-off conflicts → cannot fulfill simultaneous assignments\n• Career transition (e.g., linguist moves to different role) → weeks/months to hire replacement\n\nBenchmark:\n🟢 Excellent: Few or zero languages at 2-linguist level\n🟡 Acceptable: <15% of total languages at 2-linguist level\n🔴 Risk: >25% of languages at 2-linguist level\n\nAction plan:\n• Click to view the list of 2-linguist languages\n• Prioritize recruiting a 3rd linguist for highest-demand languages\n• For lower-demand languages, monitor workload; recruit when demand grows\n• Cross-train other linguists in adjacent languages as backup option\n\nTarget: Move all 2-linguist languages to 3+ backup within 12 months.",

              color: "warning.main",
              onClick: () =>
                openLanguageCoverageDrawer(
                  data.twoLinguistLanguages || [],
                  "Limited-Backup Languages (2 Linguists)",
                  `${(data.twoLinguistLanguages || []).length} languages with limited redundancy`,
                  "Languages currently covered by exactly two linguists. Losing one creates critical gaps.",
                ),
            },
            {
              title: "3-Linguist Languages",
              value: (data.threeLinguistLanguages || []).length,
              subtitle: "Languages with moderate coverage",
              tooltipTitle: "3-Linguist Languages",
              tooltipContent:
                "Number of languages served by exactly 3 linguists. These have moderate redundancy and reasonable continuity but can still face capacity stress.\n\nResilience profile:\n🟢 ACCEPTABLE: Can sustain loss of 1 linguist (66% capacity remaining) without complete service failure\n🟡 At capacity limit: Simultaneous assignments to 2+ linguists leaves only 1 for additional work\n\nCapacity math:\n• 1 linguist out → 66% remaining (low risk)\n• 2 linguists out (vacation, illness, resignation) → 33% remaining (fill-rate stress)\n• All 3 available → can handle only 3 concurrent assignments (may be insufficient for popular languages)\n\nWhen to expand from 3 to 4+:\n✓ Language is in top 10 by demand volume\n✓ Growing demand trend quarter-over-quarter\n✓ Client feedback requesting faster turnaround/more availability\n✓ Multiple clients requesting same language\n\nBenchmark:\n🟢 Excellent: 3 linguists is acceptable as minimum; 4+ is good scalability\n\nNext steps:\n• Click to review the 3-linguist language list\n• For languages with growing demand, recruit 4th linguist\n• For stable/niche languages, 3 may be adequate long-term\n• Monitor demand trends monthly to catch volume growth early.",

              color: "info.main",
              onClick: () =>
                openLanguageCoverageDrawer(
                  data.threeLinguistLanguages || [],
                  "Moderate-Coverage Languages (3 Linguists)",
                  `${(data.threeLinguistLanguages || []).length} languages with moderate coverage`,
                  "Languages currently covered by exactly three linguists. Some redundancy exists but growth may be beneficial.",
                ),
            },
          ],
        },
      ];
    },
    [openDirectoryDrawer, openLanguageCoverageDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const statusMap = {};
      rows.forEach((row) => {
        statusMap[row.status || "Unknown"] = (statusMap[row.status || "Unknown"] || 0) + 1;
      });

      const channelCoverage = [
        {
          id: 1,
          label: "Phone Available",
          value: rows.filter((row) => row.phoneAvailable).length,
        },
        {
          id: 2,
          label: "Virtual Available",
          value: rows.filter((row) => row.virtualAvailable).length,
        },
        {
          id: 3,
          label: "In-Person Available",
          value: rows.filter((row) => row.inPersonAvailable).length,
        },
      ];

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Channel Availability Mix"
              tooltipTitle="Channel Availability Mix"
              tooltipContent="Shows total linguist availability by delivery channel. This is useful for balancing in-person, virtual, and phone capacity with demand patterns."
              data={channelCoverage}
              height={360}
              valueFormatter={(value) => `${value || 0} linguists`}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Directory Status Distribution"
              tooltipTitle="Directory Status Distribution"
              tooltipContent="Breaks down current directory by linguist status. Monitor this distribution to ensure pipeline health and avoid over-reliance on a shrinking current pool."
              data={Object.entries(statusMap).map(([status, count]) => ({
                status,
                count,
              }))}
              xAxisKey="status"
              series={[
                {
                  dataKey: "count",
                  label: "Linguists",
                  color: chartColors.warning,
                },
              ]}
              height={320}
              yAxisLabel="Linguists"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${value || 0} linguists`}
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.warning],
  );

  return (
    <>
      <DashboardReport
        title="Linguist Directory & Language Matrix"
        description="Unified linguist profile directory with language capability coverage and multi-channel availability visibility."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Compliance & Document Health
 */
export const ComplianceDashboard = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async () => {
    const response = await api.get("/reports/linguist/compliance", { params: filters });
    return response.data;
  }, [filters]);

  const openDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "linguistName", headerName: "Linguist", width: 220 },
        { field: "status", headerName: "Status", width: 110 },
        {
          field: "complianceScore",
          headerName: "Compliance %",
          width: 120,
          renderCell: (row) => `${(row.complianceScore || 0).toFixed(1)}%`,
        },
        { field: "requiredCompleted", headerName: "Required Done", width: 120 },
        { field: "requiredTotal", headerName: "Required Total", width: 120 },
        { field: "pendingCount", headerName: "Pending", width: 90 },
        { field: "expiredCount", headerName: "Expired", width: 90 },
        { field: "expiringSoonCount", headerName: "Expiring Soon", width: 110 },
        { field: "riskLevel", headerName: "Risk", width: 90 },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const rows = data.linguistRows || [];
      const highRiskRows = rows.filter((row) => row.riskLevel === "High");

      return [
        {
          groupTitle: "Compliance Risk",
          metrics: [
            {
              title: "Average Compliance",
              value: `${(summary.averageComplianceScore || 0).toFixed(2)}%`,
              subtitle: `${summary.totalLinguists || 0} linguists assessed`,
              tooltipTitle: "Average Compliance",
              tooltipContent:
                `Average required-document completion score across linguists in scope. Lower values indicate broader compliance risk and onboarding/readiness issues.

DEFINITION:
Compliance score = (Documents submitted and verified ÷ Required documents) × 100%

This is a PORTFOLIO METRIC showing the aggregate state of your linguist credentialing/vetting across the entire directory. Not every linguist needs to be at 100% simultaneously (some in onboarding), but your portfolio average reflects overall operational readiness.

EXAMPLE SCENARIOS:
Scenario 1 (95% compliance - Excellent):
• You have 100 linguists
• Requires per-linguist: W9, background check, insurance, language certification = 4 docs minimum
• At 95%, you have 380 of 400 required documents submitted & verified
• Only 20 documents missing across entire group (like 20 linguists missing insurance renewal)
• This is healthy; most linguists are fully ready for assignment

Scenario 2 (75% compliance - Concerning):
• Same 100 linguists, 4 docs each = 400 required
• At 75%, only 300 docs submitted/verified
• 100 documents missing (like half your linguists missing certifications)
• This means ~50% of linguists cannot work until docs are resolved
• Indicates onboarding bottleneck or process failure

BENCHMARKS BY INDUSTRY/BUSINESS MODEL:
🟢 Excellent: 95%+ average compliance
→ Nearly all linguists ready for immediate assignment
→ Rare exceptions in onboarding transition
→ Client-facing impact: Can fulfill 95%+ of assignments immediately

🟡 Good: 85-94% average compliance
→ Some compliance lag is normal (10-15% in process onboarding)
→ Acceptable operational state
→ Client-facing impact: Can fulfill 85%+ immediately; minor delays for others

🟡 Acceptable: 80-84% average compliance
→ Meaningful compliance backlog emerging
→ Better track compliance closely; implement improvements
→ Client-facing impact: Estimated 20-30% of high-demand linguists blocked from assignments

🔴 Risk: 75-84% average compliance
→ Major compliance backlog
→ Indicates systemic process problem or hiring surge without supporting vetting infrastructure
→ Client-facing impact: Estimated 25-40% of linguists blocked

🔴 Critical: Below 75% average compliance
→ More than 1 in 4 linguists blocked from work
→ Company may have inadequate operations staff or broken vetting process
→ Client-facing impact: Cannot fulfill 25%+ of requests due to assignment eligibility blockers

WHAT PUSHES COMPLIANCE DOWN:
📋 Document Submission Delays  (Linguist side):
—• Linguists are independent; they procrastinate on background check forms
• Takes 2-4 weeks for background check company to return results
• International certifications may require apostille processing (30-60 days)
• Some linguists ignore reminders (need escalation contacts)

📋 Internal Verification Bottleneck (Your OPS side):
• You lack staff to review submitted documents
• Current ops person is overloaded with other duties
• No systematic workflow for document review/approval
• Example: 50 documents submitted, but only 10 reviewed/approved because someone was on vacation

📋 Certification Requirements Complexity:
• Some clients demand specific certifications (NCTA for interpreting, notary for legal docs)
• Linguist has skills but not the specific certification
• Updating certification requires time + money from linguist
• If demand is uncommon, linguist delays (other options exist)

📋 Insurance/Bonding Issues:
• Professional liability insurance policies take 2-3 weeks to activate
• Some linguists don't understand why insurance is required
• Insurance companies sometimes flag translation industry as "high-risk" (delays quotes)

ROOT CAUSE DIAGNOSIS FRAMEWORK:
If compliance dropped this period, investigate in this order:

1) New Hiring Wave (Most common reason for temporary dip)
   → Check: # of linguists added in past 60 days
   → New linguists lower compliance average until fully onboarded
   → Is this intentional (planned growth) or unplanned (unexpected hiring surge)?
   → Solution: Expected; monitor that % reaches 95% within 90 days

2) Onboarding Process Breakdown (Second most common)
   → Check: Average onboarding time from hire to 100% compliance
   → Was it 30 days last year, now 60 days?
   → Check: Do you have resources in ops to review documents?
   → Solution: Create simplified secondary language cert track; hire ops temp to clear backlogs

3) Document Expiration Wave (External timing)
   → Check: Are background checks expiring? (Renew every 3 years typical)
   → Are certifications expiring? (Usually annual)
   → Are insurance policies lapsing?
   → Solution: Implement calendar-based batch renewal process; contact linguists 60 days before expiry

4) Pending Backlog (Linguist-submitted but not verified)
   → Check: How many documents are "pending" (submitted, awaiting review)?
   → Solution: If >50 pending, you need more ops staff; if <20, this is OK

5) Specific Blocking Requirements (Process issue)
   → Check: Which documents are missing most frequently?
   → Example: If 30% of linguists missing specific certification
   → Solution: Assess if requirement is necessary for your clients; if not, simplify onboarding

CASE STUDY: Why Compliance Matters
Company A: 92% compliance, 100 linguists
• 92 linguists fully compliant, ready for assignment
• 8 linguists in onboarding (expected)
• Client requests rare-language service → pulls from ful pool → has options
• Can fulfill 95%+ of requests with qualified linguists

Company B: 75% compliance, 100 linguists
• Only 75 linguists fully compliant
• 25 linguists have missing documents (blocked from assignment)
• Client requests rare-language service → attempts rare-language linguist → finds they're blocked
• Has to reassign to less-specialized linguist -> quality issues, customer dissatisfaction
• Can only fulfill 70% of requests → other 30% rejected or delayed

Financial impact of low compliance:
• Rejected assignments = lost revenue
• Reassignment to less-qualified linguist = rework + quality issues = higher costs
• Client relationship damage = churn risk
• Operational overhead of chasing documents = cost

IF AVERAGE COMPLIANCE IS >80% BUT VARYING WIDELY:
Example: Average 88%, but ranges from 100% (some linguists) to 40% (others)
→ New hires might be in active onboarding (intentionally lower scores) - NORMAL
→ Some linguists may be backlogged with document submission requests - NEEDS ACTION
→ Consider onboarding/vetting process changes to catch issues earlier

WHAT RELATIONSHIPS TELL YOU:
• Compliance ↑ + Directory Size ↑ = Good growth + strong onboarding (healthy)
• Compliance ↓ + Directory Size ↑ = Growth exceeded ops capacity (needs fixing fast)
• Compliance ↓ + No hiring pulse = Process breakdown, not onboarding lag (urgent)
• Compliance ↑ + High Utilization ↑ = Your ready linguists are being heavily used

IMMEDIATE ACTIONS:
1. Check actual %: Is it really 85%? Or did system calculation change?
2. Look at Pending vs Blocked breakdown:
   — > Pending = you're waiting on linguists to submit (chase them)
   —> Blocked = documents submitted but you haven't reviewed (hire ops staff)
3. Audit onboarding timeline: How long from hire to 100% compliance? Target: 30 days
4. Identify which documents are missing most frequently; assess if all are necessary
5. Create renewal calendar: When do existing docs expire? Build preemptive contact plan`,

              onClick: () =>
                openDrawer(
                  "Compliance by Linguist",
                  `${rows.length} linguists`,
                  "Linguist-level compliance scores with pending/expired requirement load.",
                  rows,
                ),
            },
            {
              title: "High Risk Linguists",
              value: summary.highRiskLinguists || 0,
              subtitle: `${summary.totalExpiredDocuments || 0} expired requirements`,
              tooltipTitle: "High Risk Linguists",
              tooltipContent:
                "Linguists flagged high risk due to low required completion and/or expired requirements. Prioritize remediation here first.\n\nHigh risk criteria:\n• Compliance score < 70% (missing critical documents)\n• One or more expired certifications/documents (immediate assignment blocker)\n• Multiple pending requirements with no visible progress\n\nWhy this is urgent:\n• Expired credentials often legally prevent work assignment\n• Can cascade into client SLA breaches if discovered mid-engagement\n• Requires immediate action to maintain operational continuity\n• Can damage client trust and company reputation\n\nAction items:\n1. Click to see the high-risk queue with specific missing requirements\n2. For each linguist, contact immediately to:\n   • Determine if documents are in transit (pending)\n   • Request missing items with hard deadline\n   • Offer support (e.g., provide forms, clarify requirements)\n3. If linguist cannot comply within 14 days, consider inactivating temporarily\n4. Track remediation progress and follow up weekly\n\nTarget: Move all high-risk linguists to medium/low risk within 30 days.",

              color: "error.main",
              onClick: () =>
                openDrawer(
                  "High Risk Compliance Queue",
                  `${highRiskRows.length} linguists`,
                  "Escalation queue for compliance remediation and assignment risk control.",
                  highRiskRows,
                ),
            },
            {
              title: "Expiring in 30 Days",
              value: summary.totalExpiringSoon || 0,
              subtitle: "Upcoming compliance deadlines",
              tooltipTitle: "Expiring in 30 Days",
              tooltipContent:
                `Count of requirement items (certifications, documents, background checks) approaching expiry in the next 30 days or less. Use this to run proactive document renewal campaigns and prevent service disruption.

Why 30-day lead time matters:
• Allows time to request renewal before expiry (vs emergency mode after)
• Documents often take 2-4 weeks to obtain (background checks, certification renewals)
• Proactive approach prevents "suddenly unassignable" linguists
• Shows clients professional operations and reliability

Typical remediation timeline:
• Day 1-5: Contact linguist with friendly reminder + deadline
• Day 10: Follow up if no response
• Day 20: Escalate to manager/admin if still unresolved
• Day 28: Final notice before expiry
• Day 30+: Mark as inactive if not resolved

Best practices:
• Send automated 30-day reminders (email templates prepped)
• Batch renewal requests on specific dates (e.g., end of month)
• Maintain renewal checklist in a shared system
• Celebrate linguists who renew on schedule

Benchmark:
🟢 Excellent: <5 items expiring per month
🟡 Good: 5-10 items expiring per month
🔴 Risk: >10 items expiring per month (overwhelmed process)

Click to view the expiration calendar and prioritize outreach.`,

              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "Expiring Soon",
                  `${summary.totalExpiringSoon || 0} requirement events`,
                  "Upcoming expirations that should be resolved before assignment disruption.",
                  rows.filter((row) => (row.expiringSoonCount || 0) > 0),
                ),
            },
            {
              title: "Pending Requirements",
              value: summary.totalPending || 0,
              subtitle: "Outstanding items",
              tooltipTitle: "Pending Requirements",
              tooltipContent:
                "Total unresolved requirement items currently pending across linguists. This is your active backlog for compliance operations.\n\nPending status means:\n• Linguist has been asked to submit document but has not yet done so\n• Document submitted but awaiting organization verification\n• Awaiting external 3rd party (e.g., court, licensing board)\n• No documented action in the past 7 days\n\nWhy pending items matter:\n• Blocked linguists cannot take assignments until resolved\n• Reduces effective capacity (linguist exists but unavailable)\n• Creates operations friction (manual follow-ups, status checks)\n• Can signal onboarding/vetting process bottlenecks\n\nPending backlog benchmarks:\n🟢 Excellent: <10 pending items\n🟡 Good: 10-20 pending items\n🔴 Risk: 20-50 pending items (process overwhelmed)\n🔴 Critical: >50 pending items (major backlog)\n\nAction plan:\n1. Click to view pending items by linguist and requirement type\n2. For each item, determine root cause:\n   • Linguist not responding? Send escalation notice\n   • External delay (court, background check)? Follow up directly\n   • Unclear requirements? Provide clarification\n3. Set 14-day hard deadline for all pending items\n4. Assign ownership (who will follow up weekly)\n\nTarget: Keep pending backlog <15 items at all times (indicates healthy process flow).",

              onClick: () =>
                openDrawer(
                  "Pending Compliance Backlog",
                  `${summary.totalPending || 0} pending items`,
                  "Linguists with unresolved requirements that can impact assignment readiness.",
                  rows.filter((row) => (row.pendingCount || 0) > 0),
                ),
            },
          ],
        },
      ];
    },
    [openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Compliance Risk Distribution"
            tooltipTitle="Compliance Risk Distribution"
            tooltipContent="Distribution of linguists across compliance risk tiers. A larger high-risk share indicates potential assignment and audit exposure."
            data={data.riskDistribution || []}
            height={360}
            valueFormatter={(value) => `${value || 0} linguists`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Lowest Compliance Scores"
            tooltipTitle="Lowest Compliance Scores"
            tooltipContent="Highlights linguists with the weakest required-document completion. This is your highest-priority remediation list."
            data={(data.linguistRows || [])
              .slice()
              .sort((a, b) => (a.complianceScore || 0) - (b.complianceScore || 0))
              .slice(0, 12)
              .map((row) => ({
                linguistName: row.linguistName,
                complianceScore: row.complianceScore || 0,
              }))}
            xAxisKey="linguistName"
            series={[
              {
                dataKey: "complianceScore",
                label: "Compliance %",
                color: chartColors.error,
              },
            ]}
            height={360}
            yAxisLabel="Compliance (%)"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            showLegend={false}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ReportBarChart
            title="Requirement Coverage Gaps"
            tooltipTitle="Requirement Coverage Gaps"
            tooltipContent="Shows completion percentage by requirement type to identify systemic documentation weaknesses across the linguist network."
            data={(data.requirementCoverage || []).slice(0, 12).map((item) => ({
              requirementName: item.requirementName,
              completionRate: item.completionRate || 0,
              expired: item.expired || 0,
            }))}
            xAxisKey="requirementName"
            series={[
              {
                dataKey: "completionRate",
                label: "Completion %",
                color: chartColors.info,
              },
              {
                dataKey: "expired",
                label: "Expired Count",
                color: chartColors.warning,
              },
            ]}
            height={340}
            yAxisLabel="Coverage"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}`}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.error, chartColors.info, chartColors.warning],
  );

  return (
    <>
      <DashboardReport
        title="Compliance & Document Health"
        description="Linguist requirement completion, expiry risk, and compliance readiness in one risk-focused view."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Performance & Utilization
 */
export const LinguistPerformanceUtilization = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/linguist/performance", {
      params: activeFilters,
    });

    const rows = response.data?.data?.tableData || [];
    const totalServices = rows.reduce((sum, row) => sum + (row.servicesCount || 0), 0);
    const enhancedRows = rows.map((row) => ({
      ...row,
      utilizationShare:
        totalServices > 0 ? ((row.servicesCount || 0) / totalServices) * 100 : 0,
    }));

    return {
      data: {
        rows: enhancedRows,
      },
    };
  }, []);

  const openDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "linguistName", headerName: "Linguist", width: 220 },
        { field: "status", headerName: "Status", width: 110 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 140,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "totalPay",
          headerName: "Pay",
          width: 130,
          renderCell: (row) => formatCurrency(row.totalPay),
        },
        {
          field: "profit",
          headerName: "Profit",
          width: 130,
          renderCell: (row) => formatCurrency(row.profit),
        },
        { field: "servicesCount", headerName: "Services", width: 95 },
        {
          field: "avgRevenuePerService",
          headerName: "Avg Revenue",
          width: 140,
          renderCell: (row) => formatCurrency(row.avgRevenuePerService),
        },
        {
          field: "profitMargin",
          headerName: "Margin %",
          width: 110,
          renderCell: (row) => `${(row.profitMargin || 0).toFixed(1)}%`,
        },
        {
          field: "utilizationShare",
          headerName: "Utilization %",
          width: 115,
          renderCell: (row) => `${(row.utilizationShare || 0).toFixed(1)}%`,
        },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const totalRevenue = rows.reduce((sum, row) => sum + (row.revenue || 0), 0);
      const totalProfit = rows.reduce((sum, row) => sum + (row.profit || 0), 0);
      const totalServices = rows.reduce((sum, row) => sum + (row.servicesCount || 0), 0);
      const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
      const overUtilized = rows.filter((row) => (row.utilizationShare || 0) >= 10);

      return [
        {
          groupTitle: "Performance & Capacity",
          metrics: [
            {
              title: "Tracked Linguists",
              value: rows.length,
              subtitle: `${totalServices} services delivered`,
              tooltipTitle: "Tracked Linguists",
              tooltipContent:
                "Count of linguists with measurable service performance in the selected period. This shows how many linguists actively contributed revenue and can be analyzed for utilization and margin.\n\nThis figure differs from directory count because:\n• Not all directory linguists may have had assignments in the period\n• Seasonal patterns (e.g., some linguists only work Q4)\n• Represents active revenue contributors vs available pool\n\nKey insights:\n• If tracked count is <50% of directory, you have excess capacity (may be strategic or underutilized)\n• If tracked count is >80% of directory, your pool is heavily utilized and may face bottleneck risk\n• Ideal ratio varies by business model:\n   — High-demand business: 70-90% of directory tracked\n   — Seasonal business: 40-60% of directory tracked\n   — On-demand marketplace: 30-50% of directory tracked\n\nUse this metric to:\n• Assess what % of roster you're actually monetizing\n• Identify inactive linguists (candidates for outreach or deactivation)\n• Plan recruiting (if tracked count is too high relative to demand)\n• Benchmark productivity and capacity utilization.",

              onClick: () =>
                openDrawer(
                  "Linguist Performance Table",
                  `${rows.length} linguists`,
                  "Full performance and utilization breakdown by linguist.",
                  rows,
                ),
            },
            {
              title: "Average Margin",
              value: `${avgMargin.toFixed(2)}%`,
              subtitle: "Revenue-weighted margin",
              tooltipTitle: "Average Margin",
              tooltipContent:
                `Portfolio-level profit margin across linguist-delivered services. This is your #1 profitability KPI. Use with utilization to ensure scale does not erode profitability.

DEFINITION:
Margin = ((Linguist Revenue - Linguist Pay) ÷ Linguist Revenue) × 100%

This shows the percentage of every translation/interpretation dollar that stays with you as profit (before overhead like office, admin, tech, insurance).

Example: $100 service
• Client pays you $100
• You pay linguist $65
• Your gross profit = $35
• Margin = 35%

CRITICAL INSIGHT:
Margin is the HEALTH indicator of your business model. Revenue growth means nothing if margin is collapsing.

Example scenario:
Company A: Revenue grew 20%, Margin stayed 32% = GREAT (profit grew 20%+)
Company B: Revenue grew 20%, Margin fell to 25% = CONCERNING (profit grew only 5-10%)
Company C: Revenue grew 20%, Margin fell to 10% = DISASTER (profit barely grew; running faster on hamster wheel)

BENCHMARKS BY SERVICE MODEL:
🟢 Excellent: 35%+ net margin
→ You have pricing power or are very efficient
→ High-value services, specialized languages, or premium clients
→ Can invest in growth, staff, tools
→ 1 linguist delivering $10K revenue = you keep $3500

🟡 Good: 25-34% net margin
→ Sustainable model
→ Competitive market, but you're competitive
→ Can cover overhead + modest profit
→ Example: Market rate for translator is $50/hr, you pay $30, mark up to $45/hr = 33%

🟡 Okay: 20-24% net margin
→ Tight but survivable if overhead is low
→ Limited room for mistakes or market downturns
→ Little margin for investing back into business

🔴 Risk: 15-19% net margin
→ Pressured margins
→ Every cost increase (linguist rates, software, insurance) hurts profitability
→ May not cover all overhead expenses
→ Example: If overhead (ops staff, tools, office) is 18%, you're barely breaking even

🔴 Critical: <15% net margin
→ Unprofitable or unsustainable
→ You lose money on volume growth (more volume = bigger losses)
→ Example: 10% margin on $1M revenue = only $100K profit to cover $150K overhead = net loss

WHAT PUSHES MARGIN DOWN (Root Cause Diagnosis):
Most common causes (in order):

1️⃣ Rate Pressure from Clients (Client side)
Symptom: Margin declined but your linguist rates stayed same
→ You're charging clients less (discounted rates, aggressive RFPs)
→ Example: Used to charge $50/hr, now $42/hr for competitive bids
→ Solution: Analyze which clients/languages have lowest pricing; consider walking away from low-margin work, or negotiate volume discounts that maintain margin

2️⃣ Linguist Rate Increases (Linguist side)
Symptom: Your rates to clients stayed same, but you're paying linguists more
→ Market rate inflation (especially remote work, post-pandemic salary increases)
→ Competitive pressingfor talent (other agencies bidding)
→ Top linguists demanding raises
→ Example: Paid linguists $0.08/word 2 years ago, now $0.12/word = 33% cost increase
→ Solution: Review your pricing; if you haven't raised rates in 12+ months, you likely need to; or trim low-margin work

3️⃣ Service Mix Shift (Portfolio side)
Symptom: Revenue growing but margin declining suggests change in work types
→ Shifting to lower-margin languages (high-supply commodities like Spanish)
→ Losing high-margin specialized work (legal, medical require premium rates)
→ Shifting to lower-margin service types (general translation vs specialized interpretation)
→ Example: 30% revenue used to be legal (40% margin), now only 15% is legal (25% margin on rest)
→ Solution: Analyze revenue by language, service type, client tier; identify which segments have lowest margins; develop strategies to shift mix back

4️⃣ Operational Inefficiency (Hidden costs)
Symptom: Margin down, but rates to clients and rates to linguists look reasonable
→ More QA rework needed (quality issues = rework cost)
→ Scheduling/matching inefficiency (high turnaround time = rush fees paid to linguists)
→ Client management overhead (frequent revisions, clarifications, high-maintenance relationship)
→ Technology or payment processing friction (takes 2 weeks to get files from client, 3 weeks to pay linguist)
→ Example: Each service requires average 2 hours ops work to manage; at $25/hr = $50 burden per service; if service revenue is $200, that's another 25% cost
→ Solution: Audit 10 recent services for actual time investment; identify bottlenecks; streamline workflows

5️⃣ Language/Linguist-Specific Underperformance
Symptom: Margin down slightly, impacting specific languages or linguists
→ Certain linguist pairs are inefficient (need lots of QA rework)
→ Some languages have unfavorable rate dynamics (high pay, low client demand)
→ Example: Chinese interpretation: linguists demanding $60/hr, clients only willing to pay $75/hr = 20% margin (vs 35% on other languages)
→ Solution: Analyze margin by language; consider deprioritizing low-margin languages; or recruit linguists willing to work at different rates

MARGIN COMPRESSION DEATH SPIRAL:
🔴 DANGER ZONE - How companies go from profitable to broke:
Year 1: 35% margin, $1M revenue = $350K profit (healthy)
Year 2: Chased growth aggressively; now 30% margin on $1.5M = $450K profit (still OK, slight compression)
Year 3: Market competitive; margin down to 22% on $2.2M = $484K profit (compression accelerates)
Year 4: Caught in race-to-bottom pricing; margins collapse to 12% on $3M = $360K profit
Year 5:  At 10% on $3.5M = $350K profit (back where you started, but spending way more on ops to manage 3.5x the volume)
Result: Exhausted team, no growth in actual profit, can't afford to invest in improvements

→ → → How to prevent: Watch margin carefully; if it drops >2% in a quarter, investigate immediately

KEY RELATIONSHIPS (What to watch):
• Margin ↓ + Revenue ↑ = Danger sign (growing but less profitable)
• Margin ↓ + Retention ↓ = Double danger (paying linguists more to keep them, but not raising client rates)
• Margin ↓ + High Utilization ↑ = Efficiency problem (running harder for less profit)
• Margin ↓ + Total Revenue ↑ but Revenue per Linguist ↓ = Overstaffed or underutilized
• Margin ↑ + Directory Size ↓ = Good specialization (fewer, more profitable linguists)

IF MARGIN IS DROPPING - ROOT CAUSE FRAMEWORK:

Step 1: Segment Analysis
   — Calculate margin by: language, service type, client tier, delivery channel
   — Find which segment dropped most
   — Example: Spanish fell from 32% to 25%; Japanese stayed at 35%
   → Spanish-specific investigation needed

Step 2: Rate Analysis
   — Did you raise prices to clients? (Check if new quotes higher than old)
   — Did linguist rates increase? (Check average pay per service over time)
   → Isolate whether it's client-side or linguist-side or both

Step 3: Cost Analysis
   — Calculate cost per service (ops time, payment processing, QA, project mgmt)
   — Is it increasing?
   → If operational cost per service is rising, efficiency problem

Step 4: Volume/Utilization Analysis
   — Is linguist utilization changing?
   — Are you doing more low-volume/high-friction services?
   → If average service revenue is falling while costs stay same, efficiency problem

IMMEDIATE ACTIONS:
1. What's your margin TODAY? Write it down.
2. What was it 12 months ago? Trend is more important than absolute value.
3. Is margin trend UP (good), FLAT (monitoring), or DOWN (action needed)?
4. If DOWN: Which segment dropped most? (Debug using segmentation above)
5. Set target: If below 25%, set goal to reach 28% within 6 months through price increases OR cost reduction
6. Monthly tracking: Monitor margin weekly for sudden drops; monthly trends for gradual drift

Target: Maintain 30%+ margin. If dropping below 20%, profitability is unsustainable`,

              onClick: () =>
                openDrawer(
                  "Margin Analysis",
                  `${avgMargin.toFixed(2)}% weighted margin`,
                  "Margin mix by linguist for profitability quality analysis.",
                  rows,
                ),
            },
            {
              title: "Total Linguist Revenue",
              value: formatCurrency(totalRevenue),
              subtitle: formatCurrency(totalProfit),
              tooltipTitle: "Total Linguist Revenue",
              tooltipContent:
                "Total revenue generated by linguist-delivered services, with total profit shown in the subtitle for profitability context.\n\nRevenue insight:\n• Top-line revenue from linguist services (excluding other business lines if multi-service)\n• Includes all word counts, page rates, hourly fees, and project fees\n• Useful for:\n   — Understanding revenue concentration in linguist business line\n   — Growth tracking period-over-period\n   — Capacity planning (revenue per linguist = unit economics clarity)\n\nProfit insight:\n• Net profit = Revenue minus all linguist pay\n• Gross margin (shown in subtitle) = (Revenue - Pay) ÷ Revenue\n• Use to assess pricing power and cost efficiency\n\nKey questions:\n• Is revenue growing? (positive trend = market demand is increasing)\n• Is profit growing faster or slower than revenue? (profit growth slower = margin pressure)\n• Revenue per tracked linguist: (Total Revenue ÷ Tracked Linguists) = capacity efficiency\n   — If this is growing, you're getting better utilization or rate increases\n   — If this is declining, review pricing and assignment frequency\n\nClick to drill down by linguist to identify:\n• Top revenue contributors (your value drivers)\n• Low margin linguists (cost optimization candidates)\n• Underutilized linguists (capacity recovery opportunity).",

              color: "info.main",
              onClick: () =>
                openDrawer(
                  "Revenue Contribution by Linguist",
                  formatCurrency(totalRevenue),
                  "Revenue and profit contribution by linguist.",
                  rows,
                ),
            },
            {
              title: "High Utilization Linguists",
              value: overUtilized.length,
              subtitle: ">= 10% service share",
              tooltipTitle: "High Utilization Linguists",
              tooltipContent:
                "Linguists handling at least 10% of total service volume. Track this to prevent burnout and over-concentration risk.\n\nWhere 10% threshold comes from:\n• 1 linguist = 100% capacity (100% utilization = burnout risk)\n• 10 linguists = 10% each is balanced baseline\n• 5 linguists = 20% each (high utilization)\n• If any 1 linguist >15% → dependency/burnout risk\n\nWhy this matters:\n• Over-reliance on key linguists creates risk:\n   — If they resign, you lose 10%+ of capacity\n   — Quality may suffer if overworked\n   — Burnout → retention risk\n   — Vacation/illness creates capacity crisis\n\nConcentration risk:\n🟢 Excellent: No linguist >15% share (balanced)\n🟡 Good: 1-2 linguists at 15-20% (manageable)\n🔴 Risk: >2 linguists at 15%+ (overconcentration)\n🔴 Critical: Any linguist >25% (single-point-of-failure)\n\nAction plan:\n1. Click to see high-utilization linguists\n2. For each:\n   • Offer incentive/bonus for high production\n   • Monitor satisfaction to prevent surprise resignations\n   • Plan workload rebalancing (shift some work to other linguists)\n   • Identify backup linguist for skill/language overlap\n3. Recruit additional linguists in their specialty to reduce concentration\n\nTarget: No single linguist >15% service share.",

              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "High Utilization Watchlist",
                  `${overUtilized.length} linguists`,
                  "Linguists with elevated service share that may require workload balancing.",
                  overUtilized,
                ),
            },
          ],
        },
      ];
    },
    [openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const statusDistribution = {};
      rows.forEach((row) => {
        statusDistribution[row.status || "Unknown"] =
          (statusDistribution[row.status || "Unknown"] || 0) + 1;
      });

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Linguists by Revenue"
              tooltipTitle="Top Linguists by Revenue"
              tooltipContent="Ranks linguists by revenue generated. Use this to identify core value drivers and track contribution concentration risk."
              data={rows.slice(0, 12).map((row) => ({
                linguistName: row.linguistName,
                revenue: row.revenue || 0,
              }))}
              xAxisKey="linguistName"
              series={[
                {
                  dataKey: "revenue",
                  label: "Revenue",
                  color: chartColors.info,
                },
              ]}
              height={360}
              yAxisLabel="Revenue ($)"
              yAxisScaleType="linear"
              valueFormatter={formatCurrency}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Utilization Share by Linguist"
              tooltipTitle="Utilization Share by Linguist"
              tooltipContent="Shows each linguist's share of total service volume. Use this to balance workload and reduce dependency on a few providers."
              data={rows.slice(0, 12).map((row) => ({
                linguistName: row.linguistName,
                utilizationShare: row.utilizationShare || 0,
              }))}
              xAxisKey="linguistName"
              series={[
                {
                  dataKey: "utilizationShare",
                  label: "Utilization %",
                  color: chartColors.warning,
                },
              ]}
              height={360}
              yAxisLabel="Utilization (%)"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <ReportPieChart
              title="Linguist Status Mix"
              tooltipTitle="Linguist Status Mix"
              tooltipContent="Distribution of tracked linguists by status classification, useful for understanding active pipeline quality and transition risk."
              data={Object.entries(statusDistribution).map(([label, value], index) => ({
                id: index + 1,
                label,
                value,
              }))}
              height={320}
              valueFormatter={(value) => `${value || 0} linguists`}
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.info, chartColors.warning],
  );

  return (
    <>
      <DashboardReport
        title="Performance & Utilization"
        description="Revenue, margin, and workload-share analysis to manage linguist performance and capacity balance."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Payments & Earnings
 */
export const LinguistEarnings = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/linguist/payments", {
      params: activeFilters,
    });
    return {
      data: {
        rows: response.data?.data?.tableData || [],
      },
    };
  }, []);

  const openDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "linguistName", headerName: "Linguist", width: 220 },
        {
          field: "totalOwed",
          headerName: "Total Owed",
          width: 140,
          renderCell: (row) => formatCurrency(row.totalOwed),
        },
        {
          field: "totalPaid",
          headerName: "Total Paid",
          width: 140,
          renderCell: (row) => formatCurrency(row.totalPaid),
        },
        {
          field: "outstanding",
          headerName: "Outstanding",
          width: 140,
          renderCell: (row) => formatCurrency(row.outstanding),
        },
        { field: "servicesCount", headerName: "Services", width: 100 },
        { field: "paidServicesCount", headerName: "Paid Services", width: 120 },
        {
          field: "paymentRate",
          headerName: "Payment Rate",
          width: 115,
          renderCell: (row) => `${(row.paymentRate || 0).toFixed(1)}%`,
        },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const totalOwed = rows.reduce((sum, row) => sum + (row.totalOwed || 0), 0);
      const totalPaid = rows.reduce((sum, row) => sum + (row.totalPaid || 0), 0);
      const totalOutstanding = rows.reduce((sum, row) => sum + (row.outstanding || 0), 0);
      const totalServices = rows.reduce((sum, row) => sum + (row.servicesCount || 0), 0);
      const totalPaidServices = rows.reduce(
        (sum, row) => sum + (row.paidServicesCount || 0),
        0,
      );
      const paymentRate = totalServices > 0 ? (totalPaidServices / totalServices) * 100 : 0;

      return [
        {
          groupTitle: "Payment Health",
          metrics: [
            {
              title: "Total Owed",
              value: formatCurrency(totalOwed),
              subtitle: `${rows.length} linguists`,
              tooltipTitle: "Total Owed",
              tooltipContent:
                `Total compensation owed to linguists in scope, regardless of payment status (paid, outstanding, or still in dispute).

This is calculated as:
• All earnings from completed/invoiced services
• Minus any adjustments, deductions, or chargebacks
• Regardless of whether payment has been transmitted

Why this matters:
• Represents total liability to linguist community
• If cash doesn't exist to cover this + overhead, you have a cash flow problem
• Should be compared to cash on hand and collections

Key relationships:
• Total Owed = Total Paid + Outstanding Balance
• If Total Owed is growing faster than revenue growth, check:
   — Are you accumulating unpaid balances? (yes = cash flow risk)
   — Are linguist rates increasing without revenue rate increases? (yes = margin pressure)
   — Are payment terms extending? (yes = supplier financing increasing)

Benchmark:
🟢 Excellent: Total Owed = 15-25 days of revenue (payable within 2-4 weeks)
🟡 Good: Total Owed = 25-40 days of revenue
🔴 Risk: Total Owed = 40-60 days of revenue (serious cash management issue)
🔴 Critical: Total Owed > 60 days of revenue (insolvency risk)

Priority action: Monitor in tandem with cash collections and bank balance.`,

              onClick: () =>
                openDrawer(
                  "Linguist Payment Ledger",
                  `${rows.length} linguists`,
                  "Full earnings, paid, and outstanding breakdown by linguist.",
                  rows,
                ),
            },
            {
              title: "Total Paid",
              value: formatCurrency(totalPaid),
              subtitle: `${paymentRate.toFixed(1)}% paid service rate`,
              tooltipTitle: "Total Paid",
              tooltipContent:
                `Amount already paid out to linguists. Compare against total owed and outstanding backlog to monitor payout timeliness and cash flow efficiency.

Key metric:• Payout Rate = (Total Paid ÷ Total Owed) × 100%
   — 95%+ = excellent cash flow and operations
   — 80-94% = good, some lag is normal
   — 60-79% = concerning, significant backlog building
   — <60% = critical, major cash or administrative problem

Why payout timeliness matters:
• Linguist satisfaction. Late pay = resignation risk
• Repeat business. Linguists prioritize clients who pay on time
• Market reputation. Word spreads fast about payment reliability
• Legal/tax compliance. Inconsistent payments create audit exposure

If Total Paid is growing but Outstanding Balance is also growing:
• You're paying off old invoices but not keeping up with new ones
• Indicates operational lag or cash flow constraint
• Needs immediate attention

Target: Maintain 90%+ payout rate. If slipping below, investigate:
• Client collections delays (are you getting paid?)
• Cash flow constraints (do you have liquidity?)
• Administrative backlog (is payment processing understaffed?)
• Dispute backlog (how many invoices are in hold/under review?)`,

              color: "success.main",
              onClick: () =>
                openDrawer(
                  "Paid Payout Summary",
                  formatCurrency(totalPaid),
                  "Linguists with payment activity and completion rates.",
                  rows,
                ),
            },
            {
              title: "Outstanding Balance",
              value: formatCurrency(totalOutstanding),
              subtitle: `${rows.filter((row) => (row.outstanding || 0) > 0).length} linguists with balances`,
              tooltipTitle: "Outstanding Balance",
              tooltipContent:
                `Unpaid compensation still owed to linguists. Use this to prioritize payout queues and reduce retention/friction risk.

This represents your PAYMENT DEBT to linguist community:
• Money already earned by linguists
• Services already delivered
• Payment promised but not yet transmitted

Why outstanding balance matters:
• Directly impacts linguist retention (unpaid = resigned linguist)
• Affects recruitment (prospects ask current linguists about payment reliability)
• Operational risk (unhappy linguists rush through work, quality suffers)
• Legal exposure (unpaid wages + interest accrual)

Normal outstanding balance benchmarks:
🟢 Excellent: <10 days of revenue as outstanding (very healthy)
🟡 Good: 10-20 days of revenue as outstanding
🔴 Risk: 20-35 days of revenue as outstanding (cash flow lag)
🔴 Critical: >35 days of revenue as outstanding (major problem)

Action plan:
1. Click to view the payment queue ordered by days overdue
2. Prioritize linguists owed >30 days (pay these FIRST)
3. Set payment deadline for all outstanding:
   • Invoiced but not yet paid? 7 days max standard
   • Under dispute? Resolve within 14 days or default to payout
4. Monitor for payment backlog trend:
   • Growing? Crisis is building
   — Stable? Manageable
   • Declining? Good progress

Target: Outstanding balance < 15 days revenue.`,

              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "Outstanding Payment Queue",
                  formatCurrency(totalOutstanding),
                  "Linguists with unpaid balances requiring payout action.",
                  rows.filter((row) => (row.outstanding || 0) > 0),
                ),
            },
            {
              title: "Service Payment Rate",
              value: `${paymentRate.toFixed(2)}%`,
              subtitle: `${totalPaidServices}/${totalServices} services marked paid`,
              tooltipTitle: "Service Payment Rate",
              tooltipContent:
                `Percentage of linguist-delivered services marked as paid. This is a process KPI for payout completion and financial operations discipline.

Calculation:• Payment Rate = (Services marked paid ÷ Total services) × 100%

Example:
• 100 services delivered in period
• 92 services have been paid
• 8 services are unpaid/outstanding/in-dispute
• Payment Rate = 92% (good)

Why this matters (different from Payout Rate):
• Tracks operational completeness (are we processing payments?)
• Lower rate suggests:
   — Administrative backlog (payment processing slow)
   — Dispute backlog (quality issues, invoice discrepancies)
   — System lag (payment recorded late vs completed)

Benchmarking:
🟢 Excellent: 95%+ of services marked paid
🟡 Good: 88-94% of services marked paid
🔴 Risk: 80-87% of services marked paid
🔴 Critical: <80% of services marked paid

If payment rate is low, diagnose root cause:
• Are new services not yet processed? (lag = normal, but track aging)
• Are invoices stuck in dispute? (quality/reconciliation issue)
• Is payment processing system broken? (tech/staffing issue)
• Do linguists have valid payment holds/deductions? (justifiable but needs tracking)

Target: Maintain 92%+ service payment rate. Anything below 85% is operational risk.`,

              onClick: () =>
                openDrawer(
                  "Payment Completion Analysis",
                  `${paymentRate.toFixed(2)}% completion`,
                  "Service-level payout completion patterns by linguist.",
                  rows,
                ),
            },
          ],
        },
      ];
    },
    [openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const rows = data.rows || [];
      const totalPaid = rows.reduce((sum, row) => sum + (row.totalPaid || 0), 0);
      const totalOutstanding = rows.reduce((sum, row) => sum + (row.outstanding || 0), 0);

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Outstanding Balances"
              tooltipTitle="Top Outstanding Balances"
              tooltipContent="Ranks linguists by unpaid outstanding amount to support payout prioritization and retention protection."
              data={rows.slice(0, 12).map((row) => ({
                linguistName: row.linguistName,
                outstanding: row.outstanding || 0,
              }))}
              xAxisKey="linguistName"
              series={[
                {
                  dataKey: "outstanding",
                  label: "Outstanding",
                  color: chartColors.warning,
                },
              ]}
              height={360}
              yAxisLabel="Outstanding ($)"
              yAxisScaleType="linear"
              valueFormatter={formatCurrency}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Paid vs Outstanding"
              tooltipTitle="Paid vs Outstanding"
              tooltipContent="Compares completed payout value against remaining payout backlog. Helps monitor payout cycle efficiency and backlog pressure."
              data={[
                { id: 1, label: "Paid", value: totalPaid },
                { id: 2, label: "Outstanding", value: totalOutstanding },
              ]}
              height={360}
              valueFormatter={formatCurrency}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <ReportBarChart
              title="Payment Rate by Linguist"
              tooltipTitle="Payment Rate by Linguist"
              tooltipContent="Shows payment completion percentage by linguist. Low values can indicate reconciliation lag, process bottlenecks, or payout scheduling issues."
              data={rows.slice(0, 12).map((row) => ({
                linguistName: row.linguistName,
                paymentRate: row.paymentRate || 0,
              }))}
              xAxisKey="linguistName"
              series={[
                {
                  dataKey: "paymentRate",
                  label: "Payment Rate %",
                  color: chartColors.info,
                },
              ]}
              height={320}
              yAxisLabel="Payment Rate (%)"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.info, chartColors.warning],
  );

  return (
    <>
      <DashboardReport
        title="Payments & Earnings"
        description="Linguist compensation ledger including owed, paid, outstanding, and payout completion rates."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Client-Linguist Relationship Insights
 */
export const PreferredLinguistInsights = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/linguist/client-relationships", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const openPairDrawer = useCallback((title, subtitle, description, rows) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns: [
        { field: "clientName", headerName: "Client", width: 220 },
        { field: "linguistName", headerName: "Linguist", width: 220 },
        { field: "assignmentsCount", headerName: "Assignments", width: 110 },
        {
          field: "revenue",
          headerName: "Revenue",
          width: 130,
          renderCell: (row) => formatCurrency(row.revenue),
        },
        {
          field: "assignmentShare",
          headerName: "Client Share",
          width: 110,
          renderCell: (row) => `${(row.assignmentShare || 0).toFixed(1)}%`,
        },
        {
          field: "consistencyScore",
          headerName: "Consistency",
          width: 110,
          renderCell: (row) => `${(row.consistencyScore || 0).toFixed(1)}`,
        },
        {
          field: "cancellationRate",
          headerName: "Cancel %",
          width: 95,
          renderCell: (row) => `${(row.cancellationRate || 0).toFixed(1)}%`,
        },
        { field: "riskLevel", headerName: "Risk", width: 90 },
        {
          field: "isPreferred",
          headerName: "Preferred",
          width: 95,
          renderCell: (row) => (row.isPreferred ? "Yes" : "No"),
        },
        {
          field: "lastAssignmentDate",
          headerName: "Last Assignment",
          width: 130,
          renderCell: (row) => formatDate(row.lastAssignmentDate),
        },
      ],
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const pairRows = data.pairRows || [];
      const preferredRows = pairRows.filter((row) => row.isPreferred);

      return [
        {
          groupTitle: "Relationship Consistency",
          metrics: [
            {
              title: "Client-Linguist Pairs",
              value: summary.totalPairs || 0,
              subtitle: "Unique working relationships",
              tooltipTitle: "Client-Linguist Pairs",
              tooltipContent:
                `Total unique client-linguist relationship pairs observed in the selected period. This measures breadth of working relationships across your network.

What this metric shows:
• Unique combinations of (Client A + Linguist B) who worked together
• Includes one-off assignments and ongoing relationships
• Does NOT count repeat assignments to same pair (see Preferred Pairs)

Business insight:
• High pair count = diverse, distributed network (less concentrated dependency)
• Low pair count = narrow, concentrated relationships (high dependency risk)

Typical benchmarks:
🟢 Excellent: >100 unique pairs (highly distributed)
🟡 Good: 50-100 unique pairs (balanced)
🔴 Risk: <50 unique pairs (concentrated)

Relationship distribution analysis:
• If you have 10 clients and 50 linguists, max possible pairs = 500
• If you only have 80 pairs, you're using <20% of potential relationships
• Suggests:
   — Some linguists are unused
   — Some clients rely on limited linguist pool
   — Opportunity to distribute work more broadly

Use this alongside Preferred Pairs to understand:
• % of pairs that are recurring vs one-off
• Which linguists should be cultivated for ongoing relationships
• Where to invest in new client-linguist team building.`,

              onClick: () =>
                openPairDrawer(
                  "All Client-Linguist Pairs",
                  `${pairRows.length} relationships`,
                  "Assignment consistency and concentration by client-linguist pair.",
                  pairRows,
                ),
            },
            {
              title: "Preferred Pairs",
              value: summary.preferredPairs || 0,
              subtitle: `${pairRows.length > 0 ? (((summary.preferredPairs || 0) / pairRows.length) * 100).toFixed(1) : "0.0"}% of pairs`,
              tooltipTitle: "Preferred Pairs",
              tooltipContent:
                `PREFERRED PAIRS: Client-Linguist Relationships Achieving Sustained Continuity

Definition: A "preferred pair" is a client-linguist relationship that has achieved minimum 3+ assignments, 40%+ assignment share with that linguist, and sustained working history. These are your strongest, most efficient business relationships.

WHAT MAKES A PAIR "PREFERRED" (Specific Calculation):
• Minimum 3+ confirmed assignments together (proven they can work together)
• Assignment Share >= 40% of that client's total work assigned to that linguist (not random, but consistent)
• Multi-touch history (linguist has learned client's terminology, style, expectations, file formats, quality standards)
• Example: Client ABC assigns 30 total assignments in 90 days. Linguist X receives 13 of those 30 (43%). If they completed 3+, X~ABC is a preferred pair.
• Example 2: Client DEF assigns 5 total assignments. Linguist Y receives 2 (40%). Since they only have 2 assignments, they DON'T qualify yet (need 3 minimum).

BUSINESS VALUE BREAKDOWN (Why Preferred Pairs Matter):
1. **Quality**: Linguist embedded in client's business context
   — Knows industry terminology (medical pairs know medical terms better than generalists; legal pairs know contract language)
   — Understands client's style preferences (formal vs casual, translated vs localized, specific formatting requirements)
   — Lower quality issues = fewer revisions = less rework cost
   — Real impact: Pairs with 5+ assignment history have ~40% fewer quality revisions than new pairs

2. **Speed & Efficiency**: Drastically reduced ramp-up time
   — First assignment: 30-40 minutes to read brief, understand client, gather context = setup cost
   — Second assignment: 15-20 minutes (familiar with most, minor context)
   — Fifth assignment: 5-10 minutes (knows everything, can deliver faster)
   — Real impact: Established pairs complete assignments 20-30% faster than first-time pairs

3. **Cost & Margin**: Eliminated per-assignment overhead
   — Speed benefit directly translates to margin improvement
   — Example: 20-hour assignment. New pair = 20h work + 0.5h setup = 20.5 effective hours. Established pair = 20h work + 0.1h context = 20.1 effective hours. That's 1.5% margin difference.
   — Over 50 assignments with an established pair: 2.5 extra billable hours you're not paying for
   — Annual impact for busy pairs: $1000-3000 margin improvement per pair

4. **Retention**: Both linguists AND clients stay longer
   — Linguists: Prefer working with familiar clients (lower cognitive load, better feedback, predictable income)
   — Clients: Know they get higher quality from familiar linguist; reluctant to switch
   — Real impact: Preferred pairs have 40%+ longer retention than transactional pairs

5. **Dependency RISK**: Concentration vulnerability if pair is lost
   — If a preferred pair accounts for 10%+ of a client's work, losing that linguist = client disruption
   — If 3 preferred pairs account for 50%+ of revenue, losing any one = 15%+ revenue drop
   — Example: Client ABC has preferred pairs: X (20% of their work), Y (18%), Z (15%), Others (47%). If X leaves, ABC loses continuity for 20% of assignments.

REAL-WORLD PREFERRED PAIR EXAMPLES:
• Example 1: Legal Translation Agency, Client = Major Law Firm
  — Firm assigns 100 assignments/month. Preferred pair = Law Firm + Linguist Maria
  — Maria receives 50 of those 100 (50% share). They've worked together for 2 years (200+ assignments).
  — Maria knows the firm's legal terminology, document templates, preferred style (concise vs elaborate), formatting requirements
  — Maria's average delivery time: 19 hours for 20-hour assignment (1 hour context included)
  — Firm won't switch linguists = guaranteed revenue. Maria won't take on conflicting clients = reliable schedule.

• Example 2: Medical Translation Agency, Client = Hospital Network
  — Network assigns 40 assignments/month. Two preferred pairs: Dr. Lopez (15 assignments/month, 37%), Tech expert James (13/month, 32%)
  — Both have 3+ year history. Dr. Lopez handles patient education materials (knows medical terminology). James handles software documentation.
  — Each assignment turnaround: 18-20 hours (vs 22-24 for new translators in medical field)
  — Hospital won't use other vendors for core languages = revenue locked in

BENCHMARKS BY BUSINESS MODEL:
🟢 **Excellent: 35%+ of active pairs are preferred** (strong continuity culture)
   — Boutique agencies (specialized languages/domains): 40-60% preferred pairs
   — Medical/legal specialists: 45-65% preferred pairs (specialization rewards continuity)
   — High-volume general services: 30-40% preferred pairs (harder to achieve with diverse linguist pool)
   — Real example: Boutique 20-person agency with 60% preferred pairs = 12 strong relationships carrying ~50% of revenue

🟡 **Good: 25-34% of active pairs are preferred** (healthy but room to grow)
   — Indicates relationship development is happening; not fully transactional yet
   — Expected state for growing agencies (build from 20% → 30% → 40% over 12-24 months)

🔴 **Risk: 15-24% of active pairs are preferred** (transactional orientation)
   — Agency uses many one-off linguists = high quality variance, no specialization
   — Clients don't form relationships = low perceived loyalty, price-sensitive behavior
   — Linguists don't build expertise = slower delivery, more revisions

🔴 **Critical: <15% of active pairs are preferred** (highly transactional, relationship breakdown)
   — Either: 1) Very new agency, or 2) High turnover destroying relationships, or 3) Intentional "catalog" model
   — Margin pressure = setup costs + revisions + client friction
   — Linguist retention poor = training costs high

ROOT CAUSE FRAMEWORK: Why is Your Preferred Pair Percentage Low?

**Diagnostic Priority #1: High Linguist Turnover (Most Common)**
   — Check: What's your 12-month linguist retention rate? (See Linguist Retention Rate metric)
   — If <75%: You're losing linguists faster than preferred pairs can form
   — If high turnover = pairs broken = back to transactional model
   — Timeline: Takes 6-9 months for a pair to become "preferred" (3+ assignments). If linguists leave at month 4, they never reach preferred threshold.

**Diagnostic Priority #2: Insufficient Assignment Volume per Linguist**
   — Check: How many assignments per active linguist per month? Target = 8-12 assignments
   — If <5 assignments/month per linguist: Too dispersed. Try to concentrate linguists on specific clients.
   — If low volume, no client can give a linguist 40% of their work = preferred pairs can't form
   — Solution: Assign linguist to 3-5 primary clients instead of 20 one-off clients

**Diagnostic Priority #3: Client Churn or Rotation Strategy**
   — Check: Are YOUR clients churning/changing linguists intentionally?
   — Some agencies deliberately rotate linguists (to avoid dependency) = intentional low preferred pairs
   — If this is intentional, accept it. If accidental (clients leaving due to quality), fix quality first.

**Diagnostic Priority #4: Language/Service Mix Evolution**
   — Check: Did you add new languages/services this quarter?
   — New languages = new linguists = no preferred pairs yet (takes time)
   — New services = different linguist pool needed = breaks existing pairs
   — Timeline: New service launches take 2-3 months to build preferred pairs

**Diagnostic Priority #5: Quality/Reliability Issues Blocking Pair Formation**
   — Check: Are any linguist-client pairs being reassigned due to quality/delays?
   — If a linguist misses a deadline or delivers poor quality = client won't request them again = pair breaks
   — If low preferred pairs + high quality issues = fix quality first

CASE STUDIES: Real Impact of Preferred Pairs

**CASE A: Medical Translation Agency (Preferred Pairs = 55%)**
   — Company: 25 active linguists, 150 active client relationships
   — Preferred pairs: 82 pairs out of 150 (55%)
   — Revenue profile: 82 preferred pairs = 72% of revenue. 68 transactional pairs = 28% of revenue.
   — Key observations:
     • Medical specialists (Dr. Lopez, Pharmacist Sarah) each have 4-5 preferred pairs = 40% of individual revenue
     • Preferred pair linguists deliver 18.5-hour avg for 20-hour assignments; transactional linguists 22+ hours
     • Preferred pair clients renew at 94% rate; transactional clients renew at 60% rate
   — Margin impact: Preferred pairs = 38% margin. Transactional = 22% margin. Portfolio blended = 33%.
   — If they grew to 70% preferred pairs: Estimated new blended margin = 35% (delta = +$150K annually on $5M revenue)

**CASE B: General Translation (Preferred Pairs = 18%)**
   — Company: 45 linguists, 200 client relationships
   — Preferred pairs: 36 pairs out of 200 (18%)
   — Problem: High turnover (68% linguist retention rate). Linguists leave after 8 months, before pairs form.
   — Result: Transactional model = 24% margin, high quality variance, client friction
   — Intervention: Invested in retention (higher pay, benefits, flexible schedules) → Grew linguist retention to 82% over 12 months
   — Outcome: One year later, preferred pairs grew from 18% to 31%. Margin grew from 24% → 28%. Annual revenue impact: +$250K
   — Lesson: Retention drives preferred pairs. Preferred pairs drive margin. Retention investment has 2-year payoff cycle.

**CASE C: Boutique Legal (Preferred Pairs = 62%)**
   — Company: 12 specialists, 20 client relationships (intentional boutique model)
   — Preferred pairs: 12 out of 20 (60%)
   — Each lawyer-client pair stable for 3-5 years
   — Margin: 42% (vs industry 28%)
   — Key: Specialization allows high preferred pair %; attracts premium clients
   — Caution: Revenue concentration (top 3 clients = 65% of revenue). Losing one client = 18-25% revenue drop.

CONCENTRATION RISK ANALYSIS (The Dark Side of Preferred Pairs):
• **Scenario**: You have 50 preferred pairs generating 70% of revenue.
  — Top 5 pairs = 28% of revenue. Losing one = 5-6% revenue drop.
  — Top 10 pairs = 45% of revenue. Losing any major pair = 2-5% revenue drop.

• **Risk Pattern**: If 70%+ of revenue is in preferred pairs + those pairs are concentrated in <20 clients:
  — You MUST actively grow new preferred pairs for diversification
  — You MUST protect top pairs (higher payment, development opportunity, recognition)
  — You MUST track when pairs are at risk (linguist retirement, client feedback drop, assignment reduction)

• **"Healthy Concentration"**: 
  — 70% revenue from preferred pairs (good continuity) BUT distributed across 40+ pairs (low concentration risk)
  — Each individual pair = <5% of revenue (losing any one = minor impact)
  — Top client has preferred pairs with 5+ linguists (not dependent on single linguist)

KEY RELATIONSHIPS TO OTHER METRICS:
• **↑ Preferred Pairs % <-> ↑ Average Consistency Score**: More preferred pairs = higher consistency culture
• **↑ Preferred Pairs % <-> ↑ Average Margin**: Preferred pairs have 5-10% higher margin than transactional
• **↓ Linguist Retention Rate <-> ↓ Preferred Pairs %**: Churn breaks pairs; prevents new preferred pairs from forming
• **↑ Preferred Pairs % <-> ↓ Average Assignment Turnaround**: Faster assignments when pairs are established
• **↑ Preferred Pairs % <-> ↑ Client Retention**: Preferred pairs = higher perceived loyalty → lower churn
• **↑ Revenue in Preferred Pairs <-> ↑ Concentration Risk**: Very high concentration means few pairs carry most revenue

IMMEDIATE ACTION STEPS:

1. **TODAY: Calculate Your Current State**
   — Count: Total pairs (all relationships with 1+ assignment)
   — Count: Preferred pairs (3+ assignments AND 40%+ assignment share)
   — Calculate: Preferred % = (Preferred Pairs ÷ Total Pairs) × 100%
   — Compare to benchmark for your business model

2. **THIS WEEK: Identify Your Top 10 Preferred Pairs by Revenue**
   — Which 10 pairs are generating the most revenue?
   — Do they account for >50% of total revenue? (Concentration risk flag)
   — Which linguist-client combinations are your "crown jewels"?

3. **THIS WEEK: Assess Risk Concentration**
   — For top 5 pairs: What if one linguist left/retired? What if client reduced volume by 30%?
   — For top 10 pairs: Are they concentrated in <5 clients? (Client concentration)
   — Document your top 3 "concentration vulnerabilities"

4. **NEXT 30 DAYS: Identify Growth Opportunities**
   — Find pairs with 2 assignments (one assignment away from "preferred"). Assign them one more job together.
   — Find pairs with 40-39% assignment share (just below threshold). Push them by 1-2 more assignments.
   — Identify 10-15 "candidate pairs" that could become preferred in next 90 days.

5. **NEXT 30 DAYS: Protect Your Top Pairs**
   — Schedule check-ins with linguists in top 5 pairs (any concerns? future plans?)
   — Ask top-pair clients: "How is working with [linguist]?" Get feedback.
   — For any pair showing stress signals (cancellations, quality drop, reduced assignments), investigate & intervene.

6. **NEXT 90 DAYS: Grow Preferred Pair Percentage**
   • If currently <20%: Goal = +5% (reach 25%) = Requires 15-20 new preferred pairs
   • If currently 20-30%: Goal = +8% (reach 28-38%) = Requires 25-35 new preferred pairs
   • If currently >30%: Goal = +5% (reach 35%+) = Requires 20-30 new preferred pairs
   — To grow preferred pairs: Concentrate linguists on smaller client pools (3-5 primary clients).

7. **ONGOING: Monthly Monitoring**
   — Track preferred pair % month-over-month (should grow 1-2% per month with focus)
   — Track new pairs being formed (goal = 3-5 new preferred pairs per month)
   — Track pairs at risk of breaking (zero assignments in 30 days = at-risk pair)

TARGETS:
• **Botanical Translation Agency**: Current 25% preferred pairs → Goal 35% within 12 months = 10 new pairs + grow concentration risk management
• **Legal/Medical Specialists**: Current 35% → Goal 50%+ = Deepen specialization, build 20+ new preferred pairs
• **High-Volume Generic**: Current 18% → Goal 28% = Requires focused retention + concentrated assignment strategy

Bottom line: Preferred pairs are your business moat. They drive margin, speed, quality, and client satisfaction. Strong preferred pair culture (35%+) is mark of healthy, mature translation agency.`,

              color: "success.main",
              onClick: () =>
                openPairDrawer(
                  "Preferred Relationship Pairs",
                  `${preferredRows.length} preferred pairs`,
                  "High-consistency client-linguist relationships suitable for continuity planning.",
                  preferredRows,
                ),
            },
            {
              title: "Average Consistency",
              value: `${(summary.averageConsistencyScore || 0).toFixed(2)}`,
              subtitle: "Portfolio relationship stability score",
              tooltipTitle: "Average Consistency",
              tooltipContent:
                `Average assignment consistency score across pairs, reflecting continuity and cancellation-adjusted reliability.

Consistency Score calculation:• Score = (Assignment Share × 0.7) + (Reliability × 0.3)
• Assignment Share = % of linguist's work that goes to that client (70% weight)
• Reliability = (Assignments kept ÷ Assignments assigned) × 100 (cancellation adjusted, 30% weight)

Score interpretation:
• 90-100 = Highly consistent, reliable pair (best for continuity)
• 70-89 = Stable, mostly reliable (good working relationship)
• 50-69 = Moderate consistency (opportunistic, not preferred)
• <50 = Low consistency (one-off, transactional)

Why consistency matters:
• High score pairs = clients satisfied, linguists happy, predictable revenue
• Low score pairs = unpredictable, may churn, difficult to forecast
• Cancellations drag down score = billings issue or service quality problem

Portfolio insights:• Average score trending UP = relationships deepening (positive)
• Average score trending DOWN = relationships deteriorating (investigate)
• High variance (some 90+, some <50) = inconsistent management/relationship health

Action plan:
1. Review pairs with scores <60 (low consistency clusters)
2. For low-scoring pairs, determine if:
   — Linguist capacity inconsistency? (offer training/support)
   — Client satisfaction? (run NPS survey)
   — Language/work mismatch? (reassign to better linguist)
3. Invest in growing pairs from 60-70 to 80+ score range

Target: Average consistency score > 75 (majority of pairs are stable).`,

              onClick: () =>
                openPairDrawer(
                  "Consistency Score Distribution",
                  `${(summary.averageConsistencyScore || 0).toFixed(2)} average score`,
                  "Use this table to identify unstable or highly stable account-provider relationships.",
                  pairRows,
                ),
            },
            {
              title: "Revenue in Preferred Pairs",
              value: formatCurrency(summary.revenueInPreferredPairs || 0),
              subtitle: "Revenue concentration in continuity pairs",
              tooltipTitle: "Revenue in Preferred Pairs",
              tooltipContent:
                `Total revenue associated with preferred relationship pairs. Use this to understand how much revenue depends on relationship continuity.

Key insight:• Revenue Concentration Ratio = (Revenue in preferred pairs ÷ Total revenue) × 100%

Example:
• Total linguist revenue = $500K
• Revenue in preferred pairs = $350K
• Concentration ratio = 70%

What this means:
• 70% of your linguist revenue comes from established, repeating relationships
• 30% from one-off or inconsistent assignments

Benchmarks by business model:
🟢 Excellent: 40-60% revenue in preferred pairs (diversified + some continuity)
🟡 Good: 30-40% revenue in preferred pairs
🔴 Risk: 60-80% revenue in preferred pairs (too concentrated)
🔴 Critical: >80% revenue in preferred pairs (high dependency risk)

Concentration risk analysis:
• If 70%+ of revenue is in preferred pairs, and you lose just 3-4 major pairs, revenue drops 20-30%
• Preferred pair dissolution can happen from:
   — Linguist resignation
   — Client budget cuts or project completion
   — Relationship conflict or poor performance

Balancing act:
• Want continuity (preferred pairs are efficient and satisfying)
• But need diversity (prevent concentration risk)

Action plan:
1. Identify top 10 preferred pairs by revenue
2. Assess retention risk for each:
   — Is linguist stable? (unlikely to resign?)
   — Is client stable? (ongoing relationship?)
   — Relationship health? (NPS score, feedback positive?)
3. For high-revenue, high-risk pairs, develop backup linguist
4. Invest in growing NEW preferred pairs to diversify revenue base

Target: 40-50% revenue in preferred pairs (healthy balance of continuity + diversification).`,

              color: "info.main",
              onClick: () =>
                openPairDrawer(
                  "Preferred Pair Revenue Exposure",
                  formatCurrency(summary.revenueInPreferredPairs || 0),
                  "Revenue tied to preferred relationships for continuity and dependency planning.",
                  preferredRows,
                ),
            },
          ],
        },
      ];
    },
    [openPairDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Top Relationship Pairs by Assignment Volume"
            tooltipTitle="Top Relationship Pairs by Assignment Volume"
            tooltipContent="Ranks client-linguist pairs by assignment count. Use this to identify continuity anchors and dependency clusters."
            data={(data.pairRows || []).slice(0, 12).map((row) => ({
              pairLabel: `${row.clientName} / ${row.linguistName}`,
              assignmentsCount: row.assignmentsCount || 0,
            }))}
            xAxisKey="pairLabel"
            series={[
              {
                dataKey: "assignmentsCount",
                label: "Assignments",
                color: chartColors.info,
              },
            ]}
            height={360}
            yAxisLabel="Assignments"
            yAxisScaleType="linear"
            valueFormatter={(value) => `${value || 0} assignments`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Relationship Risk Distribution"
            tooltipTitle="Relationship Risk Distribution"
            tooltipContent="Distribution of relationship risk levels by client-linguist pair. High risk suggests inconsistent assignment continuity or elevated cancellation behavior."
            data={data.riskDistribution || []}
            height={360}
            valueFormatter={(value) => `${value || 0} pairs`}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ReportLineChart
            title="Monthly Top-Pair Share Trend"
            tooltipTitle="Monthly Top-Pair Share Trend"
            tooltipContent="Tracks average top-linguist share within each client over time. Rising values indicate stronger continuity, but extreme concentration can increase dependency risk."
            xAxisData={(data.monthlyTrend || []).map((row) => row.month)}
            series={[
              {
                data: (data.monthlyTrend || []).map((row) => row.avgTopShare || 0),
                label: "Avg Top Pair Share %",
                color: chartColors.warning,
              },
            ]}
            height={320}
            yAxisLabel="Top Pair Share (%)"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.warning],
  );

  return (
    <>
      <DashboardReport
        title="Client-Linguist Relationship Insights"
        description="Preferred-pair patterns, relationship consistency, and concentration risk between client accounts and linguists."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

// Export all linguist report components
export const LINGUIST_REPORTS = {
  "linguist-overview": LinguistOverview,
  "linguist-directory": LinguistDirectoryLanguageMatrix,
  "compliance-dashboard": ComplianceDashboard,
  "linguist-performance": LinguistPerformanceUtilization,
  "linguist-earnings": LinguistEarnings,
  "preferred-linguist": PreferredLinguistInsights,
};
